import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MdCalendarToday, MdAccessTime, MdAccountBalanceWallet, MdSave, MdClose, MdAdd, MdEdit, MdDelete, MdAttachMoney, MdPerson, MdWarning, MdCheckCircle, MdPending, MdSchedule, MdShare, MdInfoOutline, MdSettings, MdViewList, MdHistory, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import api from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getUser } from '../../services/auth';
import { t } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import { formatDateForDisplay, parseDateFromDisplay, getDateFormat } from '../../utils/dateFormat';
import { useToast } from '../../components/ToastContainer';
import ToastContainer from '../../components/ToastContainer';
import ConfirmDialog from '../../components/ConfirmDialog';
import DropdownOrBottomSheet from '../../components/DropdownOrBottomSheet';

const defaultEntryForm = {
  startTime: '09:00',
  endTime: '17:00',
  notes: '',
  hourlyRate: ''
};

const normalizeEntry = (entry) => {
  // Handle hourlyRate - preserve null/undefined, convert to number if exists
  let hourlyRate = entry.hourlyRate !== undefined ? entry.hourlyRate : entry.hourly_rate;
  if (hourlyRate !== null && hourlyRate !== undefined) {
    hourlyRate = parseFloat(hourlyRate);
    // If parseFloat returns NaN, set to null
    if (isNaN(hourlyRate)) {
      hourlyRate = null;
    }
  }
  
  return {
    id: entry.id,
    userId: entry.userId ?? entry.user_id ?? entry.ownerId ?? entry.owner_id ?? null,
    ownerId: entry.ownerId ?? entry.owner_id ?? entry.userId ?? entry.user_id ?? null,
    date: entry.date,
    startTime: entry.startTime || entry.start_time,
    endTime: entry.endTime || entry.end_time,
    notes: entry.notes || '',
    hourlyRate: hourlyRate,
    isPaid: entry.isPaid !== undefined ? entry.isPaid : (entry.is_paid || false),
    manualPaymentDate: entry.manualPaymentDate !== undefined ? entry.manualPaymentDate : (entry.manual_payment_date || null)
  };
};

const sortEntriesByStart = (entries) => [...entries].sort((a, b) => a.startTime.localeCompare(b.startTime));

/**
 * `viewAsUserId` must be the calendar owner (calendar_entries.user_id). Backend checks
 * friendships WHERE user_id = owner AND friend_id = viewer.
 * Outgoing share (you shared your calendar): entries stay under your user id — never pass your friend's id.
 * Incoming share (someone shared with you): pass that owner's id (sharedByUserId).
 */
function getEffectiveCalendarOwnerIdForView(babysitterForView) {
  if (!babysitterForView) return null;
  if (babysitterForView.isShared && babysitterForView.sharedByUserId) {
    return babysitterForView.sharedByUserId;
  }
  return null;
}

function SharedPermissionRow({ friend, onSave }) {
  const canWrite = !!(friend.shareSettings && friend.shareSettings.calendarWrite);
  const [saving, setSaving] = useState(false);
  const handleToggle = async () => {
    setSaving(true);
    try {
      await onSave(!canWrite);
    } finally {
      setSaving(false);
    }
  };
  const name = friend.name || friend.displayName || friend.email || t('friends.friend') || 'Prijatelj';
  return (
    <li className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{name}</span>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {canWrite ? (t('calendar.canWrite') || 'Može da upisuje') : (t('calendar.readOnly') || 'Samo pregled')}
        </span>
        <button
          type="button"
          onClick={handleToggle}
          disabled={saving}
          className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
        >
          {saving ? '...' : (canWrite ? (t('calendar.setReadOnly') || 'Samo pregled') : (t('calendar.setCanWrite') || 'Može da upisuje'))}
        </button>
      </div>
    </li>
  );
}

function MarkAllPaidConfirmDialog({ type, startDate, endDate, onEndDateChange, onClose, onConfirm, getEntriesPreview }) {
  const [preview, setPreview] = useState({ count: 0, amount: 0 });
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [dateInputValue, setDateInputValue] = useState('');
  const dateFormat = getDateFormat();

  useEffect(() => {
    let cancelled = false;
    getEntriesPreview().then(p => { if (!cancelled) setPreview(p); });
    return () => { cancelled = true; };
  }, [endDate, getEntriesPreview]);

  useEffect(() => {
    setAmount(preview.amount !== undefined && preview.amount !== null ? String(preview.amount) : '');
  }, [preview.amount]);

  useEffect(() => {
    if (endDate && !isNaN(endDate.getTime())) {
      setDateInputValue(formatDateForDisplay(endDate, dateFormat));
    } else {
      setDateInputValue('');
    }
  }, [endDate, dateFormat]);

  const periodLabel = type === 'today'
    ? `1. ${startDate.toLocaleDateString('sr-RS', { month: 'short' })} - danas`
    : type === 'month'
    ? startDate.toLocaleDateString('sr-RS', { month: 'long', year: 'numeric' })
    : `${startDate.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' })} - ${endDate.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' })}`;

  const handleDateChange = (e) => {
    const raw = e.target.value;
    setDateInputValue(raw);
    const parsed = parseDateFromDisplay(raw, dateFormat);
    if (parsed) onEndDateChange(parsed);
  };

  const handleConfirm = async () => {
    const numAmount = amount === '' ? preview.amount : parseFloat(String(amount).replace(',', '.'));
    if (numAmount === undefined || numAmount === null || isNaN(numAmount) || numAmount < 0) {
      return;
    }
    setLoading(true);
    try {
      await onConfirm(Math.round(numAmount * 100) / 100);
    } finally {
      setLoading(false);
    }
  };

  const confirmAmount = amount === '' ? preview.amount : parseFloat(String(amount).replace(',', '.'));
  const isValidAmount = confirmAmount !== undefined && confirmAmount !== null && !isNaN(confirmAmount) && confirmAmount >= 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Potvrdi kreiranje plaćanja
        </h3>
        {type === 'selected' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Označi kao plaćeno do datuma:
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder={dateFormat === 'dd.mm.yyyy' ? 'dd.mm.gggg' : dateFormat === 'mm/dd/yyyy' ? 'mm/dd/yyyy' : 'gggg-mm-dd'}
              value={dateInputValue}
              onChange={handleDateChange}
              className="input-base"
            />
          </div>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Označićemo <strong>{preview.count}</strong> {preview.count === 1 ? 'unos' : 'unosa'} kao plaćeno. Kreiraće se plaćanje za period <strong>{periodLabel}</strong>. Plaćanje će biti vidljivo na strani Plaćanja.
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Iznos za plaćanje (možete izmeniti):
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={formatCurrency(preview.amount)}
            className="input-base w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Predloženi iznos: {formatCurrency(preview.amount)}
          </p>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="btn-secondary">
            Otkaži
          </button>
          <button onClick={handleConfirm} disabled={loading || preview.count === 0 || !isValidAmount} className="btn-primary disabled:opacity-50 flex items-center gap-2">
            {loading ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : null}
            Potvrdi
          </button>
        </div>
      </div>
    </div>
  );
}

const timeToMinutes = (time) => {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const getDurationMinutes = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const diff = endMinutes - startMinutes;
  return Math.max(diff, 0);
};

const hasOverlap = (entries, startTime, endTime, excludeId = null) => {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);

  return entries.some((entry) => {
    if (excludeId && entry.id === excludeId) {
      return false;
    }
    // Skip placeholder entries (00:00-00:00) used only for payment dates
    if (entry.startTime === '00:00' && entry.endTime === '00:00') {
      return false;
    }
    const entryStart = timeToMinutes(entry.startTime);
    const entryEnd = timeToMinutes(entry.endTime);
    return start < entryEnd && end > entryStart;
  });
};

const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatDisplayDate = (date, monthNames) => {
  return `${date.getDate()}. ${monthNames[date.getMonth()]} ${date.getFullYear()}.`;
};

// Calculate hourly rate based on date and time
const getFlexRate = (flexibleRates, key) => {
  if (!flexibleRates) return null;
  const camel = flexibleRates[key];
  const snake = flexibleRates[key.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')];
  return camel ?? snake ?? null;
};
const calculateHourlyRate = (date, startTime, endTime, flexibleRates, defaultRate) => {
  const weekdayDaytime = getFlexRate(flexibleRates, 'weekdayDaytime');
  if (!flexibleRates || weekdayDaytime == null) {
    return defaultRate ? parseFloat(defaultRate) : 0;
  }

  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const weekend = getFlexRate(flexibleRates, 'weekend');
  const weekdayEvening = getFlexRate(flexibleRates, 'weekdayEvening') ?? weekdayDaytime;

  if (isWeekend) {
    return weekend != null ? weekend : weekdayDaytime;
  }

  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const dayStart = timeToMinutes('09:00');
  const dayEnd = timeToMinutes('17:00');

  if (startMinutes < dayStart && endMinutes > dayEnd) {
    const daytimeMinutes = dayEnd - dayStart;
    const eveningMinutes = (endMinutes - startMinutes) - daytimeMinutes;
    const totalMinutes = endMinutes - startMinutes;
    return ((daytimeMinutes * weekdayDaytime) + (eveningMinutes * weekdayEvening)) / totalMinutes;
  } else if (startMinutes >= dayStart && endMinutes <= dayEnd) {
    return weekdayDaytime;
  } else {
    return weekdayEvening;
  }
};

// Calculate rate periods for a time range
const calculateRatePeriods = (date, startTime, endTime, flexibleRates, defaultRate) => {
  const periods = [];
  
  if (!flexibleRates || !flexibleRates.weekdayDaytime) {
    const rate = defaultRate ? parseFloat(defaultRate) : 0;
    if (rate > 0) {
      const durationMinutes = getDurationMinutes(startTime, endTime);
      periods.push({
        rate: rate,
        minutes: durationMinutes,
        label: t('calendar.default') || 'Podrazumevano'
      });
    }
    return periods;
  }

  const dayOfWeek = date.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  
  if (isWeekend) {
    const durationMinutes = getDurationMinutes(startTime, endTime);
    periods.push({
      rate: flexibleRates.weekend,
      minutes: durationMinutes,
      label: t('calendar.weekend') || 'Vikend'
    });
    return periods;
  }

  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const dayStart = timeToMinutes('09:00');
  const dayEnd = timeToMinutes('17:00');

  // Case 1: Starts before dayStart and ends after dayEnd (spans entire day period)
  if (startMinutes < dayStart && endMinutes > dayEnd) {
    const beforeDayMinutes = dayStart - startMinutes;
    const daytimeMinutes = dayEnd - dayStart;
    const afterDayMinutes = endMinutes - dayEnd;
    
    if (beforeDayMinutes > 0) {
      periods.push({
        rate: flexibleRates.weekdayEvening,
        minutes: beforeDayMinutes,
        label: t('calendar.weekdayEvening') || 'Posle 17h i noću'
      });
    }
    if (daytimeMinutes > 0) {
      periods.push({
        rate: flexibleRates.weekdayDaytime,
        minutes: daytimeMinutes,
        label: t('calendar.weekdayDaytime') || 'Radni dani 9-17h'
      });
    }
    if (afterDayMinutes > 0) {
      periods.push({
        rate: flexibleRates.weekdayEvening,
        minutes: afterDayMinutes,
        label: t('calendar.weekdayEvening') || 'Posle 17h i noću'
      });
    }
  } 
  // Case 2: Starts before dayStart but ends within or at dayEnd
  else if (startMinutes < dayStart && endMinutes <= dayEnd) {
    const beforeDayMinutes = dayStart - startMinutes;
    const daytimeMinutes = endMinutes - dayStart;
    
    if (beforeDayMinutes > 0) {
      periods.push({
        rate: flexibleRates.weekdayEvening,
        minutes: beforeDayMinutes,
        label: t('calendar.weekdayEvening') || 'Posle 17h i noću'
      });
    }
    if (daytimeMinutes > 0) {
      periods.push({
        rate: flexibleRates.weekdayDaytime,
        minutes: daytimeMinutes,
        label: t('calendar.weekdayDaytime') || 'Radni dani 9-17h'
      });
    }
  }
  // Case 3: Starts within or at dayStart and ends after dayEnd (e.g., 9-18h)
  else if (startMinutes >= dayStart && endMinutes > dayEnd) {
    const daytimeMinutes = dayEnd - startMinutes;
    const afterDayMinutes = endMinutes - dayEnd;
    
    if (daytimeMinutes > 0) {
      periods.push({
        rate: flexibleRates.weekdayDaytime,
        minutes: daytimeMinutes,
        label: t('calendar.weekdayDaytime') || 'Radni dani 9-17h'
      });
    }
    if (afterDayMinutes > 0) {
      periods.push({
        rate: flexibleRates.weekdayEvening,
        minutes: afterDayMinutes,
        label: t('calendar.weekdayEvening') || 'Posle 17h i noću'
      });
    }
  }
  // Case 4: Fully within daytime (9-17h)
  else if (startMinutes >= dayStart && endMinutes <= dayEnd) {
    periods.push({
      rate: flexibleRates.weekdayDaytime,
      minutes: endMinutes - startMinutes,
      label: t('calendar.weekdayDaytime') || 'Radni dani 9-17h'
    });
  }
  // Case 5: Fully outside daytime (evening/night)
  else {
    periods.push({
      rate: flexibleRates.weekdayEvening,
      minutes: endMinutes - startMinutes,
      label: t('calendar.weekdayEvening') || 'Posle 17h i noću'
    });
  }

  return periods;
};

export default function CalendarPage() {
  const today = useMemo(() => new Date(), []);
  const [jobs, setJobs] = useState([]);
  const [calendarEntries, setCalendarEntries] = useState({}); // { dateString: [entries] }
  const [excludedPaymentDates, setExcludedPaymentDates] = useState([]); // Array of excluded payment dates (YYYY-MM-DD)
  const [manualPaymentDateTimes, setManualPaymentDateTimes] = useState([]); // Array of {date, time} objects
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [activeDate, setActiveDate] = useState(today);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // null for new entry, object for edit
  const [entryForm, setEntryForm] = useState(defaultEntryForm);
  const [defaultHourlyRate, setDefaultHourlyRate] = useState('');
  const [selectedBabysitter, setSelectedBabysitter] = useState(null);
  const [friends, setFriends] = useState([]);
  const [flexibleRates, setFlexibleRates] = useState({
    weekdayDaytime: 800, // 9-17h radnim danima
    weekdayEvening: 900, // posle 17h i noću radnim danima
    weekend: 900 // vikend
  });
  const [ratePeriods, setRatePeriods] = useState([]);
  const [manualRates, setManualRates] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, entry: null });
  const [deleteAllMonthConfirm, setDeleteAllMonthConfirm] = useState({ isOpen: false });
  const [removePaymentDateConfirm, setRemovePaymentDateConfirm] = useState({ isOpen: false });
  const [showBabysitterMenu, setShowBabysitterMenu] = useState(false);
  const [showPaymentDateModal, setShowPaymentDateModal] = useState(false);
  const [paymentTime, setPaymentTime] = useState('14:00');
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidDate, setMarkPaidDate] = useState(null);
  const [showAutoFillMenu, setShowAutoFillMenu] = useState(false);
  const [markAllPaidDialog, setMarkAllPaidDialog] = useState({ isOpen: false, type: null, endDate: null });
  const [selectedDaySheetOpen, setSelectedDaySheetOpen] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [monthAuditLogs, setMonthAuditLogs] = useState([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'calendar';
    return 'calendar';
  }); // 'calendar' | 'list' — defaults to calendar on all screen sizes
  const [sharedPermissionsOpen, setSharedPermissionsOpen] = useState(false);
  const [serverDebtSummary, setServerDebtSummary] = useState(null); // { monthlyDebt, totalDebt } when viewing shared calendar
  const autoFillMenuRef = useRef(null);
  const babysitterTriggerRef = useRef(null);
  const { showToast, removeToast, toasts } = useToast();
  const navigate = useNavigate();
  const user = getUser();
  const isMobile = useIsMobile();

  useEffect(() => {
    loadData();
  }, []);

  // Update hourly rates when selectedBabysitter changes
  useEffect(() => {
    if (selectedBabysitter) {
      const friend = typeof selectedBabysitter === 'object' 
        ? selectedBabysitter 
        : friends.find(f => f.id === selectedBabysitter || String(f.id) === String(selectedBabysitter));
      
      if (friend) {
        if (friend.hourlyRate) {
          setDefaultHourlyRate(String(friend.hourlyRate));
        }
        if (friend.flexibleRates) {
          setFlexibleRates(friend.flexibleRates);
        }
      }
    }
  }, [selectedBabysitter, friends]);

  // Lock body scroll when mobile "Izabrani dan" sheet is open (sheet is lg:hidden so mobile only)
  useEffect(() => {
    if (selectedDaySheetOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [selectedDaySheetOpen]);

  const effectiveOwnerId = useMemo(() => {
    const babysitterForView = selectedBabysitter ?? friends.find(f => f.relationshipType === 'babysitter');
    return getEffectiveCalendarOwnerIdForView(babysitterForView);
  }, [selectedBabysitter, friends]);

  const calendarEntriesQueryParams = useMemo(
    () => (effectiveOwnerId ? { viewAsUserId: effectiveOwnerId } : {}),
    [effectiveOwnerId]
  );

  const calendarMutationBody = useMemo(
    () => (effectiveOwnerId ? { viewAsUserId: effectiveOwnerId } : {}),
    [effectiveOwnerId]
  );

  const effectiveCalendarWriteAllowed = useMemo(() => {
    if (!effectiveOwnerId) return true;
    const fromFriends = friends.find((x) => String(x.id) === String(effectiveOwnerId));
    if (fromFriends?.shareSettings?.calendarWrite === true) return true;
    if (
      selectedBabysitter &&
      String(selectedBabysitter.id) === String(effectiveOwnerId) &&
      selectedBabysitter.shareSettings?.calendarWrite === true
    ) {
      return true;
    }
    if (
      selectedBabysitter?.sharedByUserId != null &&
      String(selectedBabysitter.sharedByUserId) === String(effectiveOwnerId) &&
      selectedBabysitter.shareSettings?.calendarWrite === true
    ) {
      return true;
    }
    return false;
  }, [effectiveOwnerId, friends, selectedBabysitter]);

  useEffect(() => {
    if (!effectiveOwnerId) {
      setServerDebtSummary(null);
      return;
    }
    let cancelled = false;
    api.get('/calendar-entries/debt-summary', {
      params: { viewAsUserId: effectiveOwnerId, month: viewDate.getMonth(), year: viewDate.getFullYear() }
    }).then(r => {
      if (!cancelled && r.data) setServerDebtSummary(r.data);
    }).catch(() => {
      if (!cancelled) setServerDebtSummary(null);
    });
    return () => { cancelled = true; };
  }, [effectiveOwnerId, viewDate]);

  const loadData = async (overrideBabysitter = null) => {
    try {
      // Load friends first so we know if we're viewing a shared calendar (viewAsUserId)
      const [jobsResponse, friendsResponse] = await Promise.all([
        api.get('/jobs').catch(() => ({ data: { jobs: [] } })),
        api.get('/friends').catch(() => ({ data: [] }))
      ]);
      const friendsList = Array.isArray(friendsResponse.data) ? friendsResponse.data : [];
      setJobs(jobsResponse.data?.jobs || (Array.isArray(jobsResponse.data) ? jobsResponse.data : []));
      setFriends(friendsList);

      // When someone shared their calendar with you (incoming), load that owner's calendar via viewAsUserId.
      const babysitterForView = overrideBabysitter ?? selectedBabysitter ?? friendsList.find(f => f.relationshipType === 'babysitter');
      const effectiveOwnerId = getEffectiveCalendarOwnerIdForView(babysitterForView);
      const calendarParams = effectiveOwnerId ? { viewAsUserId: effectiveOwnerId } : {};

      const [entriesResponse, excludedDatesResponse, manualDatesResponse] = await Promise.all([
        api.get('/calendar-entries', { params: calendarParams }).catch(() => ({ data: [] })),
        api.get('/calendar-entries/excluded-payment-dates', { params: calendarParams }).catch(() => ({ data: { excludedDates: [] } })),
        api.get('/calendar-entries/manual-payment-dates', { params: calendarParams }).catch(() => ({ data: { manualPaymentDateTimes: [] } }))
      ]);
      
      // Set excluded payment dates - normalize to YYYY-MM-DD format
      const excludedDates = (excludedDatesResponse.data?.excludedDates || []).map(date => {
        if (typeof date === 'string') {
          // Extract YYYY-MM-DD from ISO string or use as-is if already in correct format
          return date.split('T')[0];
        }
        return date;
      });
      setExcludedPaymentDates(excludedDates);
      if (import.meta.env.DEV) console.log('[FRONTEND] Excluded payment dates:', excludedDates);
      
      // Set manual payment dates with times
      const manualDateTimes = (manualDatesResponse.data?.manualPaymentDateTimes || []).map(item => {
        if (typeof item === 'string') {
          return { date: item.split('T')[0], time: '14:00' };
        }
        const dateValue = item?.date || item?.paymentDate || '';
        const timeValue = item?.time || item?.paymentTime || '14:00';
        return {
          date: typeof dateValue === 'string' ? dateValue.split('T')[0] : dateValue,
          time: typeof timeValue === 'string' ? timeValue.slice(0, 5) : '14:00'
        };
      }).filter(item => item.date);
      setManualPaymentDateTimes(manualDateTimes);
      if (import.meta.env.DEV) console.log('[FRONTEND] Manual payment dates with times:', manualDateTimes);
      
      // Set default hourly rate and flexible rates from first friend if babysitter, or from user if parent
      if (user?.role === 'babysitter' && friendsResponse.data && friendsResponse.data.length > 0) {
        const firstFriend = friendsResponse.data[0];
        setSelectedBabysitter(firstFriend);
        if (firstFriend.hourlyRate) {
          setDefaultHourlyRate(String(firstFriend.hourlyRate));
        }
        if (firstFriend.flexibleRates) {
          setFlexibleRates(firstFriend.flexibleRates);
        }
      } else if (user?.role === 'parent') {
        // For parents, select first babysitter friend if available; else first friend who shared calendar with me (so viewer sees correct name/amount)
        const friendsList = Array.isArray(friendsResponse.data) ? friendsResponse.data : [];
        const babysitterFriends = friendsList.filter(f => f.relationshipType === 'babysitter');
        const sharedCalendarFriends = friendsList.filter(f => !f.isShared && (f.calendarShared || (f.shareSettings && f.shareSettings.calendar)));
        const toSelect = babysitterFriends[0] ?? sharedCalendarFriends[0] ?? null;
        if (toSelect) {
          setSelectedBabysitter(toSelect);
          if (toSelect.hourlyRate) {
            setDefaultHourlyRate(String(toSelect.hourlyRate));
          }
          if (toSelect.flexibleRates) {
            setFlexibleRates(toSelect.flexibleRates);
          }
          if (toSelect.ownerDefaultHourlyRate != null && sharedCalendarFriends.includes(toSelect)) {
            setDefaultHourlyRate(String(toSelect.ownerDefaultHourlyRate));
          }
          if (toSelect.ownerFlexibleRates && sharedCalendarFriends.includes(toSelect)) {
            setFlexibleRates(toSelect.ownerFlexibleRates);
          }
        } else if (user?.hourlyRate) {
          setDefaultHourlyRate(String(user.hourlyRate));
        }
      }

      const entriesMap = {};
      const seenEntryIds = new Set(); // Track seen entry IDs to avoid duplicates
      (entriesResponse.data || []).forEach((rawEntry) => {
        const entry = normalizeEntry(rawEntry);
        // Skip if we've already seen this entry ID (duplicate)
        if (seenEntryIds.has(entry.id)) return;
        seenEntryIds.add(entry.id);
        const dateKey = new Date(entry.date).toDateString();
        if (!entriesMap[dateKey]) {
          entriesMap[dateKey] = [];
        }
        entriesMap[dateKey].push(entry);
      });

      Object.keys(entriesMap).forEach((key) => {
        entriesMap[key] = sortEntriesByStart(entriesMap[key]);
      });

      setCalendarEntries(entriesMap);

      if (effectiveOwnerId) {
        api.get('/calendar-entries/debt-summary', {
          params: { viewAsUserId: effectiveOwnerId, month: viewDate.getMonth(), year: viewDate.getFullYear() }
        }).then(r => r.data && setServerDebtSummary(r.data)).catch(() => setServerDebtSummary(null));
      } else {
        setServerDebtSummary(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateEntriesForDate = (dateKey, updater) => {
    setCalendarEntries((prev) => {
      const current = prev[dateKey] ? [...prev[dateKey]] : [];
      const updated = sortEntriesByStart(updater(current));
      return { ...prev, [dateKey]: updated };
    });
  };

  const handleSaveEntry = async (keepOpen = false) => {
    try {
      const startMinutes = timeToMinutes(entryForm.startTime);
      const endMinutes = timeToMinutes(entryForm.endTime);

      if (startMinutes >= endMinutes) {
        showToast(t('calendar.invalidRange') || 'Start time must be before end time.', 'error');
        return;
      }

      const dateKey = activeDate.toDateString();
      // Use local date format to avoid timezone shifting (e.g. CET midnight -> previous UTC day)
      const dateISO = formatDateLocal(activeDate);
      // Calculate hourly rate from manual rates if available, otherwise use flexible rates or default
      let hourlyRateValue = null;
      
      // Priority 1: Use manual rates if they exist and have values
      if (ratePeriods.length > 0 && Object.keys(manualRates).length > 0) {
        // Use weighted average from manual rates
        const totalMinutes = ratePeriods.reduce((sum, p) => sum + p.minutes, 0);
        const totalAmount = ratePeriods.reduce((sum, period, index) => {
          // Use manual rate if set, otherwise use period rate
          const rate = manualRates[index] !== undefined ? manualRates[index] : period.rate;
          return sum + ((period.minutes / 60) * rate);
        }, 0);
        hourlyRateValue = totalMinutes > 0 ? (totalAmount / (totalMinutes / 60)) : null;
      } 
      // Priority 2: Use flexible rates if available
      else if (flexibleRates && flexibleRates.weekdayDaytime) {
        hourlyRateValue = calculateHourlyRate(activeDate, entryForm.startTime, entryForm.endTime, flexibleRates, defaultHourlyRate);
      } 
      // Priority 3: Use default hourly rate
      else if (defaultHourlyRate) {
        hourlyRateValue = parseFloat(defaultHourlyRate);
      }
      
      // Always include hourlyRate in payload if we have a valid value
      const payload = {
        date: dateISO,
        startTime: entryForm.startTime,
        endTime: entryForm.endTime,
        notes: entryForm.notes,
        ...calendarMutationBody
      };
      
      if (hourlyRateValue !== null && hourlyRateValue !== undefined && !isNaN(hourlyRateValue) && hourlyRateValue > 0) {
        payload.hourlyRate = hourlyRateValue;
      }

      const existingEntries = getEntriesForDate(activeDate);
      const excludeId = editingEntry?.id || null;
      if (hasOverlap(existingEntries, entryForm.startTime, entryForm.endTime, excludeId)) {
        showToast(t('calendar.overlapError') || 'This time range overlaps with an existing entry.', 'error');
        return;
      }

      if (editingEntry?.id) {
        try {
          const response = await api.put(`/calendar-entries/${editingEntry.id}`, payload);
          const updatedEntry = normalizeEntry(response.data);
          
          updateEntriesForDate(dateKey, (current) => {
            const idx = current.findIndex((item) => item.id === updatedEntry.id);
            if (idx !== -1) {
              current[idx] = updatedEntry;
            } else {
              // If entry not found, add it
              current.push(updatedEntry);
            }
            return current;
          });
        } catch (error) {
          console.error('Error updating entry:', error);
          const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || error.message || (t('calendar.updateError') || 'Greška pri ažuriranju unosa. Pokušajte ponovo.');
          showToast(errorMessage, 'error');
          return;
        }
      } else {
        try {
          const response = await api.post('/calendar-entries', payload);
          const newEntry = normalizeEntry(response.data);
          
          updateEntriesForDate(dateKey, (current) => [...current, newEntry]);
        } catch (error) {
          console.error('Error creating entry:', error);
          const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || error.message || 'Greška pri kreiranju unosa. Pokušajte ponovo.';
          showToast(errorMessage, 'error');
          return;
        }
      }

      if (keepOpen) {
        setEntryForm(defaultEntryForm);
        setEditingEntry(null);
        return;
      }

      setShowEditModal(false);
      setEditingEntry(null);
      setEntryForm(defaultEntryForm);
    } catch (error) {
      console.error('Error saving entry:', error);
    }
  };

  const handleDeleteEntry = (entry) => {
    setDeleteConfirm({ isOpen: true, entry });
  };

  const confirmDeleteEntry = async () => {
    const entry = deleteConfirm.entry;
    if (!entry) return;
    
    const dateKey = new Date(entry.date).toDateString();
    try {
      await api.delete(`/calendar-entries/${entry.id}`, {
        data: calendarMutationBody,
        ...(effectiveOwnerId != null ? { params: { viewAsUserId: effectiveOwnerId } } : {})
      });
      updateEntriesForDate(dateKey, (current) => current.filter((item) => item.id !== entry.id));
      
      // Close delete confirmation dialog
      setDeleteConfirm({ isOpen: false, entry: null });
      
      // Reload calendar entries to refresh month overview
      await loadData();
      
      showToast(t('calendar.deleteSuccess') || 'Unos je uspešno obrisan.', 'success');
    } catch (error) {
      console.error('Error deleting entry:', error);
      const errorMessage = error.response?.data?.message || t('calendar.deleteError') || 'Greška pri brisanju unosa. Pokušajte ponovo.';
      showToast(errorMessage, 'error');
    }
  };

  const confirmDeleteAllMonth = async () => {
    try {
      const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
      const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
      const startDateISO = formatDateLocal(firstDayOfMonth);
      const endDateISO = formatDateLocal(lastDayOfMonth);

      // Get all entries for this month
      const allEntriesResponse = await api.get('/calendar-entries', { params: calendarEntriesQueryParams });
      const allEntries = Array.isArray(allEntriesResponse.data) ? allEntriesResponse.data : [];
      
      const entriesToDelete = allEntries.filter(e => {
        const eDate = new Date(e.date).toISOString().split('T')[0];
        return eDate >= startDateISO && eDate <= endDateISO;
      });

      // Delete all entries for this month
      const deleteOpts = {
        data: calendarMutationBody,
        ...(effectiveOwnerId != null ? { params: { viewAsUserId: effectiveOwnerId } } : {})
      };
      const deletePromises = entriesToDelete.map(entry =>
        api.delete(`/calendar-entries/${entry.id}`, deleteOpts)
      );

      await Promise.all(deletePromises);
      await loadData();
      setShowAutoFillMenu(false);
      setDeleteAllMonthConfirm({ isOpen: false });
      showToast(t('calendar.deleteAllMonthSuccess')?.replace('{count}', entriesToDelete.length) || `Obrisano ${entriesToDelete.length} unosa za ovaj mesec.`, 'success');
    } catch (error) {
      console.error('Error deleting entries:', error);
      setDeleteAllMonthConfirm({ isOpen: false });
      showToast(t('calendar.deleteAllMonthError') || 'Greška pri brisanju unosa: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  // Helper function to format date as YYYY-MM-DD in local timezone (avoid timezone issues)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!activityLogOpen) return;
    let cancelled = false;
    (async () => {
      setActivityLogLoading(true);
      try {
        const y = viewDate.getFullYear();
        const m = viewDate.getMonth();
        const from = formatDateLocal(new Date(y, m, 1));
        const to = formatDateLocal(new Date(y, m + 1, 0));
        const r = await api.get('/calendar-entries/audit-log', {
          params: {
            ...calendarEntriesQueryParams,
            fromDate: from,
            toDate: to,
            limit: 500
          }
        });
        if (!cancelled) {
          setMonthAuditLogs(Array.isArray(r.data?.logs) ? r.data.logs : []);
        }
      } catch {
        if (!cancelled) setMonthAuditLogs([]);
      } finally {
        if (!cancelled) setActivityLogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activityLogOpen, viewDate, calendarEntriesQueryParams]);

  const canMutateCalendarEntry = (entry) => {
    const ownerId = entry?.ownerId ?? entry?.userId;
    if (ownerId == null || user?.id == null) return false;
    if (String(ownerId) === String(user.id)) return true;
    // Unos je u bazi pod user_id prijatelja (npr. dadilja ga uneo), a vi ste vlasnik prijateljstva koji ste podelili kalendar
    const outgoingSharedFriendRow = friends.find(
      (f) =>
        !f.isShared &&
        (f.calendarShared || f.shareSettings?.calendar) &&
        String(f.id) === String(ownerId)
    );
    if (outgoingSharedFriendRow) return true;
    if (effectiveOwnerId != null && effectiveCalendarWriteAllowed) {
      return true;
    }
    return false;
  };

  const handleSetPaymentDate = async () => {
    try {
      // Use active date as payment date (naredni datum plaćanja)
      // Use local date format to avoid timezone issues
      const dateISO = formatDateLocal(activeDate);
      await api.put(`/calendar-entries/day/${dateISO}/payment-date`, {
        paymentDate: dateISO,
        paymentTime: paymentTime || '14:00',
        ...calendarMutationBody
      });
      
      await loadData();
      setShowPaymentDateModal(false);
      setPaymentTime('14:00');
      showToast(t('calendar.paymentDateSet') || 'Datum plaćanja je postavljen.', 'success');
    } catch (error) {
      console.error('Error setting payment date:', error);
      const errorMessage = error.response?.data?.message || error.message || t('calendar.paymentDateError') || 'Greška pri postavljanju datuma plaćanja.';
      showToast(errorMessage, 'error');
    }
  };

  const handleRemovePaymentDate = async () => {
    try {
      // Use local date format to avoid timezone issues
      const dateISO = formatDateLocal(activeDate);
      if (import.meta.env.DEV) console.log('[FRONTEND] Removing payment date for:', dateISO);
      if (import.meta.env.DEV) console.log('[FRONTEND] Active date:', activeDate);
      
      const response = await api.put(`/calendar-entries/day/${dateISO}/payment-date`, {
        paymentDate: null,
        ...calendarMutationBody
      });
      
      if (import.meta.env.DEV) console.log('[FRONTEND] Response received:', response.data);
      
      // If backend returns allEntries, use them directly to update state immediately
      if (response.data?.allEntries && Array.isArray(response.data.allEntries)) {
        if (import.meta.env.DEV) console.log('[FRONTEND] Updating calendar entries with', response.data.allEntries.length, 'entries');
        const entriesMap = {};
        const seenEntryIds = new Set();
        response.data.allEntries.forEach((rawEntry) => {
          const entry = normalizeEntry(rawEntry);
          if (seenEntryIds.has(entry.id)) {
            return;
          }
          seenEntryIds.add(entry.id);
          const dateKey = new Date(entry.date).toDateString();
          if (!entriesMap[dateKey]) {
            entriesMap[dateKey] = [];
          }
          entriesMap[dateKey].push(entry);
        });
        
        Object.keys(entriesMap).forEach((key) => {
          entriesMap[key] = sortEntriesByStart(entriesMap[key]);
        });
        
        if (import.meta.env.DEV) console.log('[FRONTEND] Setting calendar entries:', entriesMap);
        setCalendarEntries(entriesMap);
      }
      
      // Also reload all data to ensure everything is in sync
      if (import.meta.env.DEV) console.log('[FRONTEND] Reloading all data...');
      await loadData();
      if (import.meta.env.DEV) console.log('[FRONTEND] Data reloaded');
      
      showToast(t('calendar.paymentDateRemoved') || 'Datum plaćanja je uklonjen.', 'success');
    } catch (error) {
      console.error('[FRONTEND] Error removing payment date:', error);
      console.error('[FRONTEND] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || error.message || t('calendar.paymentDateError') || 'Greška pri uklanjanju datuma plaćanja.';
      showToast(errorMessage, 'error');
    }
  };

  const formatAuditActionLabel = (action) => {
    switch (action) {
      case 'entry_created': return t('calendar.auditEntryCreated') || 'Unos kreiran';
      case 'entry_updated': return t('calendar.auditEntryUpdated') || 'Unos izmenjen';
      case 'entry_deleted': return t('calendar.auditEntryDeleted') || 'Unos obrisan';
      case 'entry_marked_paid': return t('calendar.auditEntryMarkedPaid') || 'Dan označen kao plaćen';
      case 'entry_marked_unpaid': return t('calendar.auditEntryMarkedUnpaid') || 'Dan označen kao neplaćen';
      case 'payment_date_set': return t('calendar.auditPaymentDateSet') || 'Datum plaćanja postavljen';
      case 'payment_date_removed': return t('calendar.auditPaymentDateRemoved') || 'Datum plaćanja uklonjen';
      default: return action || (t('calendar.auditUnknown') || 'Izmena');
    }
  };

  const parseAuditDetails = (raw) => {
    if (raw == null) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return null;
  };

  const formatAuditLogDetailLine = (log) => {
    const d = parseAuditDetails(log.details);
    const parts = [];
    if (log.entryId != null) {
      parts.push(`${t('calendar.auditEntryRef') || 'Unos'} #${log.entryId}`);
    }
    if (d && typeof d === 'object') {
      const st = d.startTime ?? d.start_time;
      const et = d.endTime ?? d.end_time;
      if (st && et) {
        parts.push(`${st}–${et}`);
      }
      const notes = d.notes != null ? String(d.notes).trim() : '';
      if (notes) {
        parts.push(notes.length > 80 ? `${notes.slice(0, 80)}…` : notes);
      }
      const hr = d.hourlyRate ?? d.hourly_rate;
      if (hr != null && hr !== '') {
        parts.push(`${hr} RSD/h`);
      }
      if (d.isPaid !== undefined) {
        parts.push(
          d.isPaid
            ? (t('calendar.markedPaidShort') || 'označeno plaćeno')
            : (t('calendar.markedUnpaidShort') || 'označeno neplaćeno')
        );
      }
      const pd = d.paymentDate ?? d.payment_date;
      if (pd) {
        const pds = typeof pd === 'string' ? pd.split('T')[0] : String(pd);
        const pt = d.paymentTime ?? d.payment_time;
        parts.push(
          `${t('calendar.paymentShort') || 'Plaćanje'}: ${pds}${pt && String(pt).trim() ? ` ${String(pt).slice(0, 5)}` : ''}`
        );
      }
    }
    return parts.length ? parts.join(' · ') : null;
  };

  const openPaymentDateModal = () => {
    setShowPaymentDateModal(true);
  };

  const openNewEntryModal = (date) => {
    setActiveDate(date);
    setEditingEntry(null);
    setEntryForm(defaultEntryForm);
    // Calculate rate periods for default times (9:00-17:00)
    const periods = calculateRatePeriods(date, defaultEntryForm.startTime, defaultEntryForm.endTime, flexibleRates, defaultHourlyRate);
    setRatePeriods(periods);
    const manual = {};
    periods.forEach((period, index) => {
      manual[index] = period.rate;
    });
    setManualRates(manual);
    setShowEditModal(true);
  };

  const openEditEntryModal = (entry) => {
    const dateObj = new Date(entry.date);
    setActiveDate(dateObj);
    setEditingEntry(entry);
    
    // Calculate hourly rate from entry if it exists, otherwise use calculated rate
    let displayHourlyRate;
    if (entry.hourlyRate !== null && entry.hourlyRate !== undefined && entry.hourlyRate > 0) {
      displayHourlyRate = String(entry.hourlyRate);
    } else {
      // Calculate from flexible rates or default
      const periods = calculateRatePeriods(dateObj, entry.startTime, entry.endTime, flexibleRates, defaultHourlyRate);
      if (periods.length > 0) {
        const totalMinutes = periods.reduce((sum, p) => sum + p.minutes, 0);
        const totalAmount = periods.reduce((sum, p) => sum + ((p.minutes / 60) * p.rate), 0);
        const avgRate = totalMinutes > 0 ? (totalAmount / (totalMinutes / 60)) : 0;
        displayHourlyRate = avgRate > 0 ? String(avgRate) : defaultHourlyRate || '';
      } else {
        displayHourlyRate = defaultHourlyRate || '';
      }
    }
    
    setEntryForm({
      startTime: entry.startTime,
      endTime: entry.endTime,
      notes: entry.notes || '',
      hourlyRate: displayHourlyRate
    });
    // Calculate rate periods for existing entry
    if (entry.startTime && entry.endTime) {
      const periods = calculateRatePeriods(dateObj, entry.startTime, entry.endTime, flexibleRates, defaultHourlyRate);
      setRatePeriods(periods);
      const manual = {};
      periods.forEach((period, index) => {
        // If entry has hourlyRate, use it; otherwise use period rate
        if (entry.hourlyRate !== null && entry.hourlyRate !== undefined && entry.hourlyRate > 0) {
          // Distribute the hourlyRate proportionally across periods
          manual[index] = entry.hourlyRate;
        } else {
          manual[index] = period.rate;
        }
      });
      setManualRates(manual);
    }
    setShowEditModal(true);
  };

  const getEntriesForDate = (date) => {
    return calendarEntries[date.toDateString()] || [];
  };

  const weekdayKeyFromDate = (date) => {
    const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return map[date.getDay()] || null;
  };

  // Use UTC "date-only" keys to avoid DST edge cases when calculating week differences.
  const utcDayKey = (date) => {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  };

  const weeksBetween = (fromDate, toDate) => {
    const diffMs = utcDayKey(toDate) - utcDayKey(fromDate);
    return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
  };

  const normalizeDateOnly = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      const datePart = value.includes('T') ? value.split('T')[0] : value;
      const dt = new Date(datePart);
      if (Number.isNaN(dt.getTime())) return null;
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 0, 0, 0, 0);
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);
    }
    return null;
  };

  // Check if a date is a payment day based on payment schedule
  const isPaymentDay = (date, paymentSchedule) => {
    if (!paymentSchedule) return false;
    
    const dayOfMonth = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    
    // Support new format (date + time + repeatType)
    if (paymentSchedule.date) {
      const scheduleDate = normalizeDateOnly(paymentSchedule.date);
      if (!scheduleDate) return false;
      const scheduleDay = scheduleDate.getDate();
      
      if (paymentSchedule.repeatType === 'daily') {
        // Every day
        return true;
      } else if (paymentSchedule.repeatType === 'weekly') {
        // Same day of week (optionally every N weeks)
        if (date.getDay() !== scheduleDate.getDay()) return false;
        const interval = parseInt(paymentSchedule.intervalWeeks, 10);
        const normalizedInterval = Number.isFinite(interval) && interval > 1 ? interval : 1;
        if (normalizedInterval === 1) return true;
        const diffWeeks = weeksBetween(scheduleDate, date);
        return diffWeeks >= 0 && diffWeeks % normalizedInterval === 0;
      } else if (paymentSchedule.repeatType === 'monthly') {
        // Same day of month
        if (scheduleDay === 1) {
          return dayOfMonth === 1;
        } else if (scheduleDay === lastDayOfMonth && scheduleDate.getDate() === new Date(scheduleDate.getFullYear(), scheduleDate.getMonth() + 1, 0).getDate()) {
          // Last day of month
          return dayOfMonth === lastDayOfMonth;
        } else {
          return dayOfMonth === scheduleDay;
        }
      }
    }
    
    // Support old format (backward compatibility)
    if (paymentSchedule.scheduleType === 'weekly') {
      const days = Array.isArray(paymentSchedule.days) ? paymentSchedule.days : [];
      const weekdayKey = weekdayKeyFromDate(date);
      if (!weekdayKey) return false;
      const matches = days.includes(weekdayKey);
      if (!matches) return false;

      const interval = parseInt(paymentSchedule.intervalWeeks, 10);
      const normalizedInterval = Number.isFinite(interval) && interval > 1 ? interval : 1;
      const anchor = normalizeDateOnly(paymentSchedule.date);
      // If we have an anchor date, we can honor intervalWeeks; otherwise fall back to every week.
      if (normalizedInterval > 1 && anchor) {
        const diffWeeks = weeksBetween(anchor, date);
        return diffWeeks >= 0 && diffWeeks % normalizedInterval === 0;
      }
      return true;
    }

    if (paymentSchedule.scheduleType === 'everyNth') {
      const nth = paymentSchedule.everyNthDay || paymentSchedule.dayOfMonth || 1;
      return dayOfMonth % nth === 0 || dayOfMonth === nth;
    } else {
      // specificDays - support multiple days (e.g., ['15', 'last'])
      const days = paymentSchedule.days || (paymentSchedule.dayOfMonth ? [paymentSchedule.dayOfMonth] : [1]);
      return days.some(d => {
        if (d === 'first') return dayOfMonth === 1;
        if (d === 'fifteenth' || d === '15') return dayOfMonth === 15;
        if (d === 'last') return dayOfMonth === lastDayOfMonth;
        // Handle numeric strings like '15'
        const numDay = typeof d === 'string' ? parseInt(d) : d;
        if (!isNaN(numDay)) return dayOfMonth === numDay;
        return dayOfMonth === d;
      });
    }
  };

  // Get payment schedule for selected babysitter
  const getPaymentSchedule = () => {
    if (!selectedBabysitter) {
      // Fallback to first babysitter friend if available
      const firstBabysitter = friends.find(f => f.relationshipType === 'babysitter');
      return firstBabysitter?.paymentSchedule || null;
    }
    // selectedBabysitter can be an object (friend) or an ID
    const friend = typeof selectedBabysitter === 'object' 
      ? selectedBabysitter 
      : friends.find(f => f.id === selectedBabysitter || String(f.id) === String(selectedBabysitter));
    return friend?.paymentSchedule || null;
  };
  
  // Calculate next payment date based on calendar entries (manual payment dates) and payment schedule
  const nextPaymentDate = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    
    // First, check manual payment dates from API (with times). Support both date and paymentDate keys.
    const manualPaymentDatesFromAPI = [];
    manualPaymentDateTimes.forEach((item) => {
      const dateStr = (item && (item.date || item.paymentDate)) ? (item.date || item.paymentDate) : null;
      if (!dateStr) return;
      const dateOnlyStr = typeof dateStr === 'string' && dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const parts = dateOnlyStr.split('-').map(Number);
      if (parts.length < 3 || parts.some(Number.isNaN)) return;
      const [year, month, day] = parts;
      const timeStr = item.time || item.paymentTime || '14:00';
      const [hours, mins] = (typeof timeStr === 'string' ? timeStr : '14:00').split(':').map(Number);
      const paymentDate = new Date(year, month - 1, day, Number.isFinite(hours) ? hours : 14, Number.isFinite(mins) ? mins : 0, 0, 0);
      if (isNaN(paymentDate.getTime())) return;

      const paymentDateOnly = new Date(year, month - 1, day, 0, 0, 0, 0);
      const isToday = paymentDateOnly.getTime() === todayStart.getTime();
      const isFuture = paymentDateOnly > todayStart;
      const timePassed = isToday && now.getHours() * 60 + now.getMinutes() >= (Number.isFinite(hours) ? hours : 14) * 60 + (Number.isFinite(mins) ? mins : 0);

      if (isFuture || (isToday && !timePassed)) {
        manualPaymentDatesFromAPI.push(paymentDate);
      }
    });
    
    // Also check all calendar entries for manual payment dates
    const manualPaymentDatesFromEntries = [];
    
    Object.values(calendarEntries).forEach((entries) => {
      entries.forEach(entry => {
        if (entry.manualPaymentDate) {
          // Handle both string and Date formats
          let paymentDate;
          if (typeof entry.manualPaymentDate === 'string') {
            // Parse string date (YYYY-MM-DD format)
            if (entry.manualPaymentDate.includes('T')) {
              paymentDate = new Date(entry.manualPaymentDate);
            } else {
              const parts = entry.manualPaymentDate.split('-');
              if (parts.length === 3) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const day = parseInt(parts[2], 10);
                paymentDate = new Date(year, month, day, 14, 0, 0, 0); // Default to 14:00
              } else {
                paymentDate = new Date(entry.manualPaymentDate + 'T14:00:00');
              }
            }
          } else {
            paymentDate = new Date(entry.manualPaymentDate);
            paymentDate.setHours(14, 0, 0, 0); // Default to 14:00
          }
          
          if (isNaN(paymentDate.getTime())) return;
          
          const paymentDateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate(), 0, 0, 0, 0);
          const isToday = paymentDateOnly.getTime() === todayStart.getTime();
          const isFuture = paymentDateOnly > todayStart;
          const timePassed = isToday && now.getHours() * 60 + now.getMinutes() >= paymentDate.getHours() * 60 + paymentDate.getMinutes();
          
          if (isFuture || (isToday && !timePassed)) {
            manualPaymentDatesFromEntries.push(paymentDate);
          }
        }
      });
    });
    
    // Combine both sources
    const combinedManualPaymentDates = [...manualPaymentDatesFromAPI, ...manualPaymentDatesFromEntries];
    
    // Also check payment schedule dates
    const paymentSchedule = getPaymentSchedule();
    const schedulePaymentDates = [];
    
    if (paymentSchedule) {
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const currentDay = now.getDate();
      
      // Support new format (date + time + repeatType)
      if (paymentSchedule.date) {
        // Parse anchor as local date (avoid UTC shift for "YYYY-MM-DD")
        const raw = paymentSchedule.date;
        let scheduleDate = null;
        if (typeof raw === 'string') {
          const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
          const parts = datePart.split('-').map(Number);
          if (parts.length >= 3 && parts[0] && parts[1] && parts[2]) {
            scheduleDate = new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
          }
        }
        if (!scheduleDate || isNaN(scheduleDate.getTime())) {
          scheduleDate = normalizeDateOnly(paymentSchedule.date) || new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        }
        const scheduleDay = scheduleDate.getDate();

        // Calculate next occurrence based on repeatType
        if (paymentSchedule.repeatType === 'daily') {
          // Next occurrence is tomorrow
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);
          schedulePaymentDates.push(tomorrow);
        } else if (paymentSchedule.repeatType === 'weekly') {
          const interval = parseInt(String(paymentSchedule.intervalWeeks || 1).trim(), 10);
          const normalizedInterval = Number.isFinite(interval) && interval > 1 ? interval : 1;
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

          if (normalizedInterval > 1) {
            // Recurrence: scheduleDate + k * (interval * 7) for all integers k. Next = first in sequence >= today.
            const dayMs = 24 * 60 * 60 * 1000;
            const periodDays = normalizedInterval * 7;
            const daysBetween = (todayStart.getTime() - scheduleDate.getTime()) / dayMs;
            let k = Math.ceil(daysBetween / periodDays);
            const nextInSequence = new Date(scheduleDate.getTime());
            nextInSequence.setDate(nextInSequence.getDate() + k * periodDays);
            if (nextInSequence.getTime() < todayStart.getTime()) {
              nextInSequence.setDate(nextInSequence.getDate() + periodDays);
            }
            schedulePaymentDates.push(nextInSequence);
          } else {
            // Every week: next occurrence of same weekday (today counts if same weekday)
            const scheduleDayOfWeek = scheduleDate.getDay();
            const currentDayOfWeek = now.getDay();
            let daysUntilNext = scheduleDayOfWeek - currentDayOfWeek;
            if (daysUntilNext < 0) daysUntilNext += 7;
            const candidate = new Date(todayStart);
            candidate.setDate(candidate.getDate() + daysUntilNext);
            if (candidate.getTime() < todayStart.getTime()) {
              candidate.setDate(candidate.getDate() + 7);
            }
            schedulePaymentDates.push(candidate);
          }
        } else if (paymentSchedule.repeatType === 'monthly') {
          // Next occurrence is same day of month
          if (currentDay < scheduleDay) {
            // This month - check if day exists in current month
            const lastDayOfCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
            const targetDay = Math.min(scheduleDay, lastDayOfCurrentMonth);
            schedulePaymentDates.push(new Date(currentYear, currentMonth, targetDay));
          } else {
            // Next month
            const nextMonth = currentMonth + 1;
            const lastDayOfNextMonth = new Date(currentYear, nextMonth + 1, 0).getDate();
            const targetDay = Math.min(scheduleDay, lastDayOfNextMonth);
            schedulePaymentDates.push(new Date(currentYear, nextMonth, targetDay));
          }
        }
      } else {
        // Support old format (backward compatibility)
        if (paymentSchedule.scheduleType === 'weekly') {
          const days = Array.isArray(paymentSchedule.days) ? paymentSchedule.days : [];
          const validWeekdays = days.filter(d => ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].includes(d));
          // For weekly schedules without an anchor, find the next occurrence among selected weekdays.
          if (validWeekdays.length > 0) {
            validWeekdays.forEach((weekday) => {
              const map = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
              const targetDow = map[weekday];
              if (targetDow === undefined) return;
              const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
              let diff = (targetDow - todayStart.getDay() + 7) % 7;
              const candidate = new Date(todayStart);
              candidate.setDate(candidate.getDate() + diff);
              schedulePaymentDates.push(candidate);
            });
          }
        } else {
          // Default: monthly day-of-month behavior
          const days = paymentSchedule.days || (paymentSchedule.dayOfMonth ? [paymentSchedule.dayOfMonth] : [1]);
          const lastDayOfCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
          
          // Process all days in the schedule
          days.forEach(day => {
            let targetDay;
            if (day === 'first') {
              targetDay = 1;
            } else if (day === 'last') {
              targetDay = lastDayOfCurrentMonth;
            } else if (day === 'fifteenth' || day === '15') {
              targetDay = 15;
            } else {
              const numDay = typeof day === 'string' ? parseInt(day) : day;
              if (!isNaN(numDay)) {
                targetDay = numDay;
              } else {
                targetDay = 1;
              }
            }
            
            // Ensure targetDay is valid for the month
            const validTargetDay = Math.min(targetDay, lastDayOfCurrentMonth);
            
            if (currentDay < validTargetDay) {
              // This month
              schedulePaymentDates.push(new Date(currentYear, currentMonth, validTargetDay));
            } else {
              // Next month
              const nextMonth = currentMonth + 1;
              const lastDayOfNextMonth = new Date(currentYear, nextMonth + 1, 0).getDate();
              const nextValidTargetDay = Math.min(targetDay, lastDayOfNextMonth);
              schedulePaymentDates.push(new Date(currentYear, nextMonth, nextValidTargetDay));
            }
          });
        }
      }
    }
    
    // Helper function to check if a date is excluded
    const isDateExcluded = (date) => {
      const dateStr = formatDateLocal(date);
      return excludedPaymentDates.includes(dateStr);
    };
    
    // Combine all payment dates and find the earliest one.
    // Schedule dates: respect excluded list (user may have removed those from schedule).
    // Manual payment dates: always include (user explicitly set them; they are the source of truth for "next").
    const allPaymentDates = [];
    schedulePaymentDates.forEach(scheduleDate => {
      if (scheduleDate && !isNaN(scheduleDate.getTime())) {
        scheduleDate.setHours(0, 0, 0, 0);
        const dateOnly = new Date(scheduleDate.getFullYear(), scheduleDate.getMonth(), scheduleDate.getDate(), 0, 0, 0, 0);
        if (dateOnly >= todayStart && !isDateExcluded(scheduleDate)) {
          allPaymentDates.push(scheduleDate);
        }
      }
    });

    combinedManualPaymentDates.forEach(date => {
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
      allPaymentDates.push(dateOnly);
    });
    
    if (allPaymentDates.length === 0) {
      return null;
    }
    
    // Remove duplicates by converting to ISO string and back, then sort
    // Use a Map to ensure we get the earliest date for each unique day
    const dateMap = new Map();
    allPaymentDates.forEach(date => {
      const dateKey = date.toISOString().split('T')[0];
      if (!dateMap.has(dateKey) || date.getTime() < dateMap.get(dateKey).getTime()) {
        dateMap.set(dateKey, date);
      }
    });
    
    // Convert to array and sort
    const uniqueDates = Array.from(dateMap.values());
    uniqueDates.sort((a, b) => a.getTime() - b.getTime());
    
    // Return the earliest future date
    return uniqueDates.length > 0 ? uniqueDates[0] : null;
  }, [today, selectedBabysitter, friends, calendarEntries, excludedPaymentDates, manualPaymentDateTimes]);
  
  // Calculate days until payment (date-only comparison)
  const daysUntilPayment = useMemo(() => {
    if (!nextPaymentDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const paymentDate = new Date(nextPaymentDate);
    paymentDate.setHours(0, 0, 0, 0);
    const diffTime = paymentDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [nextPaymentDate]);

  // Helper function to calculate next payment date for a specific babysitter
  const getNextPaymentForBabysitter = useMemo(() => {
    return (babysitter) => {
      if (!babysitter || !babysitter.paymentSchedule) return null;
      const schedule = babysitter.paymentSchedule;
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      
      // Get manual payment dates for this babysitter from calendar entries and manualPaymentDateTimes
      const manualDates = [];
      
      // Check manualPaymentDateTimes (from API) - these might be associated with babysitter
      manualPaymentDateTimes.forEach(({ date, time, friendId }) => {
        if (date && (!friendId || friendId === babysitter.id)) {
          const [year, month, day] = date.split('-').map(Number);
          const [hours, mins] = (time || '14:00').split(':').map(Number);
          const paymentDate = new Date(year, month - 1, day, hours || 14, mins || 0, 0, 0);
          if (!isNaN(paymentDate.getTime())) {
            const dateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate(), 0, 0, 0, 0);
            if (dateOnly >= todayStart) {
              manualDates.push(dateOnly);
            }
          }
        }
      });
      
      // Check calendar entries for manual payment dates
      Object.values(calendarEntries).flat().forEach(entry => {
        if (entry.manualPaymentDate && (entry.babysitterId === babysitter.id || entry.friendId === babysitter.id)) {
          let paymentDate;
          if (typeof entry.manualPaymentDate === 'string') {
            const parts = entry.manualPaymentDate.split('-');
            if (parts.length === 3) {
              paymentDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 14, 0, 0, 0);
            } else {
              paymentDate = new Date(entry.manualPaymentDate + 'T14:00:00');
            }
          } else {
            paymentDate = new Date(entry.manualPaymentDate);
            paymentDate.setHours(14, 0, 0, 0);
          }
          if (!isNaN(paymentDate.getTime())) {
            const dateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate(), 0, 0, 0, 0);
            if (dateOnly >= todayStart) {
              manualDates.push(dateOnly);
            }
          }
        }
      });

      // Calculate schedule dates (simplified version)
      const scheduleDates = [];
      if (schedule.date) {
        const scheduleDate = normalizeDateOnly(schedule.date) || new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        const scheduleDay = scheduleDate.getDate();
        const currentDay = now.getDate();
        
        if (schedule.repeatType === 'monthly') {
          if (currentDay < scheduleDay) {
            scheduleDates.push(new Date(now.getFullYear(), now.getMonth(), scheduleDay));
          } else {
            const nextMonth = now.getMonth() + 1;
            scheduleDates.push(new Date(now.getFullYear(), nextMonth, scheduleDay));
          }
        } else if (schedule.repeatType === 'weekly') {
          const scheduleDayOfWeek = scheduleDate.getDay();
          const currentDayOfWeek = now.getDay();
          let daysUntilNext = scheduleDayOfWeek - currentDayOfWeek;
          if (daysUntilNext < 0) daysUntilNext += 7;
          const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
          candidate.setDate(candidate.getDate() + daysUntilNext);
          scheduleDates.push(candidate);
        }
      }

      // Combine and find earliest
      const allDates = [...manualDates, ...scheduleDates].filter(d => {
        const dateStr = formatDateLocal(d);
        return !excludedPaymentDates.includes(dateStr);
      });
      
      if (allDates.length === 0) return null;
      allDates.sort((a, b) => a.getTime() - b.getTime());
      return allDates[0];
    };
  }, [calendarEntries, excludedPaymentDates, manualPaymentDateTimes]);

  // Check if payment is completed for period up to this date (scheduled payment day and/or manual payment date)
  const isPaymentCompleted = (date, paymentSchedule) => {
    const dateStr = formatDateLocal(date);

    const hasManualPaymentForDate =
      manualPaymentDateTimes.some(({ date: d }) => {
        if (!d) return false;
        const ds = typeof d === 'string' ? d.split('T')[0] : formatDateLocal(new Date(d));
        return ds === dateStr;
      }) ||
      Object.values(calendarEntries).flat().some((entry) => {
        if (!entry.manualPaymentDate) return false;
        let paymentDate;
        if (typeof entry.manualPaymentDate === 'string') {
          if (entry.manualPaymentDate.includes('T')) {
            paymentDate = new Date(entry.manualPaymentDate);
          } else {
            const parts = entry.manualPaymentDate.split('-');
            if (parts.length === 3) {
              paymentDate = new Date(
                parseInt(parts[0], 10),
                parseInt(parts[1], 10) - 1,
                parseInt(parts[2], 10)
              );
            } else {
              paymentDate = new Date(entry.manualPaymentDate + 'T00:00:00');
            }
          }
        } else {
          paymentDate = new Date(entry.manualPaymentDate);
        }
        if (isNaN(paymentDate.getTime())) return false;
        return formatDateLocal(paymentDate) === dateStr;
      });

    const isScheduledPaymentDay =
      paymentSchedule &&
      isPaymentDay(date, paymentSchedule) &&
      !excludedPaymentDates.includes(dateStr);

    if (!hasManualPaymentForDate && !isScheduledPaymentDay) return false;

    const allEntries = Object.entries(calendarEntries)
      .filter(([entryDateStr]) => {
        const entryDate = new Date(entryDateStr);
        return entryDate <= date;
      })
      .flatMap(([, entries]) => entries);

    if (allEntries.length === 0) return false;

    return allEntries.every((entry) => entry.isPaid === true || entry.is_paid === true);
  };

  // Calculate total amount for entries up to a payment date
  const calculateAmountForPaymentDate = (paymentDate) => {
    const allEntries = Object.entries(calendarEntries)
      .filter(([entryDateStr]) => {
        const entryDate = new Date(entryDateStr);
        return entryDate <= paymentDate;
      })
      .flatMap(([, entries]) => entries)
      .filter(entry => {
        // Only count unpaid entries
        const isPaid = entry.isPaid === true || entry.is_paid === true;
        return !isPaid;
      })
      .filter((entry) => canMutateCalendarEntry(entry));

    let total = 0;
    allEntries.forEach(entry => {
      if (entry.startTime === '00:00' && entry.endTime === '00:00') return; // Skip placeholder entries
      const minutes = getDurationMinutes(entry.startTime, entry.endTime);
      const hours = minutes / 60;
      const rate = entry.hourlyRate || parseFloat(defaultHourlyRate) || 0;
      total += hours * rate;
    });

    return total;
  };

  /**
   * Unpaid entries (excluding 00:00-00:00 placeholders) in date range that belong to the
   * effective calendar owner (own calendar OR friend's shared calendar). Used for both the
   * suggested amount in the "mark all paid" dialog and the list of entries to update.
   * The actual `mark-paid` PUTs are gated by `canMutateCalendarEntry` at call-site.
   */
  const getEntriesToMarkAsPaid = (allEntries, startDateISO, endDateISO) => {
    return allEntries.filter((e) => {
      const ownerId = e.owner_id ?? e.user_id ?? e.ownerId ?? e.userId;
      if (ownerId == null || user?.id == null) return false;
      const belongsToActiveCalendar =
        String(ownerId) === String(user.id) ||
        (effectiveOwnerId != null && String(ownerId) === String(effectiveOwnerId));
      if (!belongsToActiveCalendar) return false;
      if (e.startTime === '00:00' && e.endTime === '00:00') return false;
      const eDate = new Date(e.date).toISOString().split('T')[0];
      return eDate >= startDateISO && eDate <= endDateISO && !(e.isPaid || e.is_paid);
    });
  };

  // Calculate amount for entries (same logic as calculateAmountForPaymentDate but for a list)
  const calculateAmountForEntries = (entries) => {
    let total = 0;
    entries.forEach(entry => {
      if (entry.startTime === '00:00' && entry.endTime === '00:00') return;
      const minutes = getDurationMinutes(entry.startTime, entry.endTime);
      const hours = minutes / 60;
      const rate = entry.hourlyRate || entry.hourly_rate || parseFloat(defaultHourlyRate) || 0;
      total += hours * rate;
    });
    return total;
  };

  // Open mark as paid modal
  const openMarkPaidModal = (date) => {
    const amount = calculateAmountForPaymentDate(date);
    setMarkPaidAmount(amount > 0 ? amount.toFixed(2) : '0.00');
    setMarkPaidDate(date);
    setShowMarkPaidModal(true);
  };

  // Handle mark as paid with payment creation
  const handleMarkAsPaidWithPayment = async () => {
    if (!markPaidDate) return;
    
    try {
      const amount = parseFloat(markPaidAmount);
      if (isNaN(amount) || amount <= 0) {
        showToast(t('calendar.invalidAmount') || 'Unesite validan iznos.', 'error');
        return;
      }

      // Get all unpaid entries up to payment date
      const allEntries = Object.entries(calendarEntries)
        .filter(([entryDateStr]) => {
          const entryDate = new Date(entryDateStr);
          return entryDate <= markPaidDate;
        })
        .flatMap(([, entries]) => entries)
        .filter(entry => {
          const isPaid = entry.isPaid === true || entry.is_paid === true;
          return !isPaid && !(entry.startTime === '00:00' && entry.endTime === '00:00') && canMutateCalendarEntry(entry);
        });

      // Calculate period dates
      const periodStart = allEntries.length > 0 
        ? new Date(Math.min(...allEntries.map(e => new Date(e.date).getTime())))
        : new Date(markPaidDate.getFullYear(), markPaidDate.getMonth(), 1);
      // Create payment
      const paymentData = {
        amount: amount,
        date: formatDateLocal(markPaidDate),
        method: 'bank_transfer',
        description: `Plaćanje za ${markPaidDate.toLocaleDateString('sr-RS', { month: 'long', year: 'numeric' })}`,
        status: 'paid',
        periodStartDate: formatDateLocal(periodStart),
        periodEndDate: formatDateLocal(markPaidDate),
        ...calendarMutationBody
      };

      await api.post('/payments', paymentData);

      // Mark all entries as paid
      const markPromises = allEntries.map(entry => 
        api.put(`/calendar-entries/${entry.id}/mark-paid`, { isPaid: true, ...calendarMutationBody })
      );
      await Promise.all(markPromises);

      // Reload data
      await loadData();
      
      setShowMarkPaidModal(false);
      setMarkPaidAmount('');
      setMarkPaidDate(null);
      showToast(t('calendar.paymentMarkedAsPaid') || 'Plaćanje je označeno kao plaćeno i kreiran je zapis.', 'success');
    } catch (error) {
      console.error('Error marking as paid with payment:', error);
      showToast(t('calendar.markPaidError') || 'Greška pri označavanju kao plaćeno: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const monthNames = [
    t('calendar.january'), t('calendar.february'), t('calendar.march'), t('calendar.april'),
    t('calendar.may'), t('calendar.june'), t('calendar.july'), t('calendar.august'),
    t('calendar.september'), t('calendar.october'), t('calendar.november'), t('calendar.december')
  ];

  // Start week with Monday (not Sunday)
  const dayNames = [
    t('calendar.monday'), t('calendar.tuesday'), t('calendar.wednesday'),
    t('calendar.thursday'), t('calendar.friday'), t('calendar.saturday'), t('calendar.sunday')
  ];

  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();
  const firstDay = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0);
  const daysInMonth = lastDay.getDate();
  // Adjust to start week with Monday (0 = Sunday, 1 = Monday, etc.)
  const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0

  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Calculate working days in current month (Monday-Friday)
  const getWorkingDaysInMonth = () => {
    let workingDays = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewYear, viewMonth, day);
      const dayOfWeek = date.getDay();
      // Monday = 1, Friday = 5
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        workingDays++;
      }
    }
    return workingDays;
  };

  const workingDaysThisMonth = getWorkingDaysInMonth();

  const selectedMonthEntries = Object.entries(calendarEntries)
    .filter(([dateKey]) => {
      const date = new Date(dateKey);
      return date.getMonth() === viewMonth && date.getFullYear() === viewYear;
    })
    .sort((a, b) => new Date(a[0]) - new Date(b[0]));

  // Calculate total hours for current month (all entries, not just unpaid)
  const totalMonthMinutes = selectedMonthEntries.reduce((sum, [, entries]) => (
    sum + entries.reduce((inner, entry) => inner + getDurationMinutes(entry.startTime, entry.endTime), 0)
  ), 0);

  const monthTotalHours = totalMonthMinutes / 60;

  // Calculate unpaid hours for current month
  const unpaidMonthMinutes = selectedMonthEntries.reduce((sum, [, entries]) => (
    sum + entries
      .filter(entry => !(entry.isPaid || entry.is_paid))
      .reduce((inner, entry) => inner + getDurationMinutes(entry.startTime, entry.endTime), 0)
  ), 0);

  const unpaidMonthHours = unpaidMonthMinutes / 60;

  // Calculate realized working days (days with entries)
  const realizedWorkingDays = new Set(
    selectedMonthEntries
      .filter(([dateStr, entries]) => {
        const date = new Date(dateStr);
        const dayOfWeek = date.getDay();
        // Only count weekdays (Monday-Friday)
        return dayOfWeek >= 1 && dayOfWeek <= 5 && entries.length > 0;
      })
      .map(([dateStr]) => dateStr)
  ).size;

  // Calculate debt from jobs
  const jobsDebt = jobs
    .filter((job) => job.status === 'in_progress' || job.status === 'completed')
    .filter((job) => job.paymentStatus === 'pending')
    .reduce((sum, job) => sum + (job.totalAmount || 0), 0);

  // Calculate monthly debt for CURRENT MONTH: only UNPAID entries
  const unpaidMonthEntries = selectedMonthEntries
    .flatMap(([dateStr, entries]) => {
      const entryDate = new Date(dateStr);
      return entries
        .filter((entry) => {
          // Only include unpaid entries
          const isPaid = entry.isPaid === true || entry.is_paid === true;
          return !isPaid;
        })
        .map(entry => ({ entry, date: entryDate }));
    });

  // Convert defaultHourlyRate to number for consistent calculations (used in both monthlyDebt and entriesDebt)
  const numericDefaultHourlyRate = typeof defaultHourlyRate === 'string' 
    ? (defaultHourlyRate ? parseFloat(defaultHourlyRate) : 0)
    : (defaultHourlyRate || 0);

  // When viewing shared calendar (either via shared babysitter or friend who shared with me), use entry.hourlyRate and owner's default so debt matches owner's view.
  // sharedCalendarBabysitterName / ownerDefaultHourlyRate are set by API when owner shared calendar with current user (e.g. parent shared with babysitter Dunja).
  const isViewingSharedCalendar = !!(selectedBabysitter && (
    (selectedBabysitter.isShared && selectedBabysitter.sharedByUserId) ||
    selectedBabysitter.calendarShared ||
    selectedBabysitter.shareSettings?.calendar ||
    !!selectedBabysitter.sharedCalendarBabysitterName ||
    (selectedBabysitter.ownerDefaultHourlyRate != null && selectedBabysitter.ownerDefaultHourlyRate > 0)
  ));
  const ownerDefaultRate = (selectedBabysitter?.ownerDefaultHourlyRate != null && selectedBabysitter?.ownerDefaultHourlyRate > 0)
    ? (typeof selectedBabysitter.ownerDefaultHourlyRate === 'string' ? parseFloat(selectedBabysitter.ownerDefaultHourlyRate) : selectedBabysitter.ownerDefaultHourlyRate)
    : (selectedBabysitter?.hourlyRate ? (typeof selectedBabysitter.hourlyRate === 'string' ? parseFloat(selectedBabysitter.hourlyRate) : selectedBabysitter.hourlyRate) : 0);
  // When babysitter views shared calendar, use owner's flexible rates so total debt matches parent's view (ownerFlexibleRates from API when parent shared)
  const rawOwnerFlexible = selectedBabysitter?.ownerFlexibleRates ?? selectedBabysitter?.flexibleRates;
  const ownerFlexibleRates = rawOwnerFlexible && (rawOwnerFlexible.weekdayDaytime != null || rawOwnerFlexible.weekday_daytime != null)
    ? {
        weekdayDaytime: rawOwnerFlexible.weekdayDaytime ?? rawOwnerFlexible.weekday_daytime,
        weekdayEvening: rawOwnerFlexible.weekdayEvening ?? rawOwnerFlexible.weekday_evening,
        weekend: rawOwnerFlexible.weekend
      }
    : rawOwnerFlexible;
  const useOwnerFlexibleForDebt = isViewingSharedCalendar && ownerFlexibleRates && getFlexRate(ownerFlexibleRates, 'weekdayDaytime') != null;
  const ownerNumericRate = typeof ownerDefaultRate === 'number' && !isNaN(ownerDefaultRate) ? ownerDefaultRate : 0;

  const monthlyDebt = useOwnerFlexibleForDebt
    ? unpaidMonthEntries.reduce((sum, { entry, date }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        const rate = calculateHourlyRate(date, entry.startTime, entry.endTime, ownerFlexibleRates, ownerNumericRate);
        return sum + (durationHours * (rate || 0));
      }, 0)
    : isViewingSharedCalendar
    ? unpaidMonthEntries.reduce((sum, { entry }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        let rate = entry.hourlyRate != null ? (typeof entry.hourlyRate === 'string' ? parseFloat(entry.hourlyRate) : entry.hourlyRate) : ownerDefaultRate;
        if (rate === null || rate === undefined) rate = ownerDefaultRate;
        return sum + (durationHours * (rate && !isNaN(rate) ? rate : 0));
      }, 0)
    : flexibleRates && flexibleRates.weekdayDaytime
    ? unpaidMonthEntries.reduce((sum, { entry, date }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        const rate = calculateHourlyRate(date, entry.startTime, entry.endTime, flexibleRates, numericDefaultHourlyRate);
        return sum + (durationHours * (rate || 0));
      }, 0)
    : unpaidMonthEntries.reduce((sum, { entry }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        // Use entry's hourlyRate if available, otherwise defaultHourlyRate
        const rate = entry.hourlyRate || numericDefaultHourlyRate;
        // Convert rate to number if it's a string
        const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;
        if (numericRate && numericRate > 0 && !isNaN(numericRate)) {
          return sum + (durationHours * numericRate);
        }
        return sum;
      }, 0);

  // Calculate debt from ALL UNPAID calendar entries (all months) - use EXACT same logic as monthlyDebt
  const allUnpaidEntries = Object.entries(calendarEntries)
    .flatMap(([dateStr, entries]) => {
      const entryDate = new Date(dateStr);
      return entries
        .filter((entry) => {
          // Only include unpaid entries - same filter as monthlyDebt
          const isPaid = entry.isPaid === true || entry.is_paid === true;
          return !isPaid;
        })
        .map(entry => ({ entry, date: entryDate }));
    });

  // Verify that all current month entries are included in allUnpaidEntries
  const currentMonthEntriesInAll = allUnpaidEntries.filter(({ date }) => 
    date.getMonth() === viewMonth && date.getFullYear() === viewYear
  );
  
  // Recalculate monthly debt from allUnpaidEntries to ensure consistency
  const monthlyDebtFromAll = useOwnerFlexibleForDebt
    ? currentMonthEntriesInAll.reduce((sum, { entry, date }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        const rate = calculateHourlyRate(date, entry.startTime, entry.endTime, ownerFlexibleRates, ownerNumericRate);
        return sum + (durationHours * (rate || 0));
      }, 0)
    : isViewingSharedCalendar
    ? currentMonthEntriesInAll.reduce((sum, { entry }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        let rate = entry.hourlyRate != null ? (typeof entry.hourlyRate === 'string' ? parseFloat(entry.hourlyRate) : entry.hourlyRate) : ownerDefaultRate;
        if (rate === null || rate === undefined) rate = ownerDefaultRate;
        return sum + (durationHours * (rate && !isNaN(rate) ? rate : 0));
      }, 0)
    : flexibleRates && flexibleRates.weekdayDaytime
    ? currentMonthEntriesInAll.reduce((sum, { entry, date }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        const rate = calculateHourlyRate(date, entry.startTime, entry.endTime, flexibleRates, numericDefaultHourlyRate);
        return sum + (durationHours * (rate || 0));
      }, 0)
    : currentMonthEntriesInAll.reduce((sum, { entry }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        let rate = entry.hourlyRate;
        if (rate === null || rate === undefined || rate === '') {
          rate = numericDefaultHourlyRate;
        }
        const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;
        if (numericRate && numericRate > 0 && !isNaN(numericRate)) {
          return sum + (durationHours * numericRate);
        }
        return sum;
      }, 0);

  // Use the recalculated monthly debt to ensure consistency
  const finalMonthlyDebt = monthlyDebtFromAll > 0 ? monthlyDebtFromAll : monthlyDebt;
  
  // The real fix: ensure currentDebt includes all unpaid entries from all months
  // Current debt should be: monthly debt + debt from other months
  // Calculate debt from other months (all months except current)
  const otherMonthsEntries = allUnpaidEntries.filter(({ date }) => 
    !(date.getMonth() === viewMonth && date.getFullYear() === viewYear)
  );
  
  const otherMonthsDebt = useOwnerFlexibleForDebt
    ? otherMonthsEntries.reduce((sum, { entry, date }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        const rate = calculateHourlyRate(date, entry.startTime, entry.endTime, ownerFlexibleRates, ownerNumericRate);
        return sum + (durationHours * (rate || 0));
      }, 0)
    : isViewingSharedCalendar
    ? otherMonthsEntries.reduce((sum, { entry }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        let rate = entry.hourlyRate != null ? (typeof entry.hourlyRate === 'string' ? parseFloat(entry.hourlyRate) : entry.hourlyRate) : ownerDefaultRate;
        if (rate === null || rate === undefined) rate = ownerDefaultRate;
        return sum + (durationHours * (rate && !isNaN(rate) ? rate : 0));
      }, 0)
    : flexibleRates && flexibleRates.weekdayDaytime
    ? otherMonthsEntries.reduce((sum, { entry, date }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        const rate = calculateHourlyRate(date, entry.startTime, entry.endTime, flexibleRates, numericDefaultHourlyRate);
        return sum + (durationHours * (rate || 0));
      }, 0)
    : otherMonthsEntries.reduce((sum, { entry }) => {
        const durationMinutes = getDurationMinutes(entry.startTime, entry.endTime);
        const durationHours = durationMinutes / 60;
        let rate = entry.hourlyRate;
        if (rate === null || rate === undefined || rate === '') {
          rate = numericDefaultHourlyRate;
        }
        const numericRate = typeof rate === 'string' ? parseFloat(rate) : rate;
        if (numericRate && numericRate > 0 && !isNaN(numericRate)) {
          return sum + (durationHours * numericRate);
        }
        return sum;
      }, 0);
  
  // Current debt = monthly debt + other months debt + jobs debt
  // This ensures total is always >= monthly debt
  const currentDebt = finalMonthlyDebt + otherMonthsDebt + jobsDebt;

  // When viewing someone else's calendar (shared), use server debt only — same source as owner; do not add viewer's jobs debt.
  const useServerDebtForDisplay = effectiveOwnerId != null;
  const displayMonthlyDebt = useServerDebtForDisplay
    ? (serverDebtSummary != null ? serverDebtSummary.monthlyDebt : null)
    : finalMonthlyDebt;
  const displayTotalDebt = useServerDebtForDisplay
    ? (serverDebtSummary != null ? serverDebtSummary.totalDebt : null)
    : currentDebt;

  // Primary label = čije smene gledate (dadilja / prijatelj); "Podeljeno od" je posebno ispod kada je isShared
  const isViewingFriendSharedCalendar = !!(selectedBabysitter && (selectedBabysitter.calendarShared || selectedBabysitter.shareSettings?.calendar) && !selectedBabysitter.isShared);
  const calendarDisplayName = !selectedBabysitter
    ? null
    : user?.role === 'babysitter'
      ? (selectedBabysitter.sharedCalendarBabysitterName
          || selectedBabysitter.name
          || selectedBabysitter.email
          || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
          || (t('calendar.babysitter') || 'Bebisiterka'))
      : (selectedBabysitter.sharedCalendarBabysitterName)
        ? selectedBabysitter.sharedCalendarBabysitterName
        : (isViewingFriendSharedCalendar
            ? ((t('calendar.title') || 'Kalendar') + ' (' + (selectedBabysitter.name || selectedBabysitter.email || '') + ')')
            : (selectedBabysitter.name || selectedBabysitter.email));

  if (import.meta.env.DEV) {
    if (selectedBabysitter && (selectedBabysitter.calendarShared || selectedBabysitter.shareSettings?.calendar || selectedBabysitter.isShared)) {
      console.log('[Calendar] shared calendar view:', {
        selectedId: selectedBabysitter.id,
        selectedName: selectedBabysitter.name,
        relationshipType: selectedBabysitter.relationshipType,
        calendarShared: selectedBabysitter.calendarShared,
        shareSettingsCalendar: selectedBabysitter.shareSettings?.calendar,
        sharedCalendarBabysitterName: selectedBabysitter.sharedCalendarBabysitterName,
        isShared: selectedBabysitter.isShared,
        sharedByUserId: selectedBabysitter.sharedByUserId,
        effectiveOwnerId,
        serverDebtSummary,
        displayMonthlyDebt,
        displayTotalDebt,
        calendarDisplayName
      });
    }
  }

  const handleMonthNavigation = (direction) => {
    const next = new Date(viewYear, viewMonth + direction, 1);
    setViewDate(next);
    setActiveDate(next);
  };

  const selectedDayEntries = getEntriesForDate(activeDate);
  // Separate placeholder entries (payment date only) from real time entries
  const placeholderEntries = selectedDayEntries.filter(entry => 
    entry.startTime === '00:00' && entry.endTime === '00:00' && entry.manualPaymentDate
  );
  // Also find any other placeholder entries (00:00-00:00) without manualPaymentDate that are paid
  // These might be leftover entries that need to be cleaned up
  const otherPlaceholderEntries = selectedDayEntries.filter(entry => 
    entry.startTime === '00:00' && entry.endTime === '00:00' && !entry.manualPaymentDate
  );
  // Filter out placeholder entries (00:00-00:00) for display purposes
  const displayEntries = selectedDayEntries.filter(entry => 
    !(entry.startTime === '00:00' && entry.endTime === '00:00')
  );
  const selectedDayMinutes = displayEntries.reduce((sum, entry) => sum + getDurationMinutes(entry.startTime, entry.endTime), 0);
  
  // Check if day has manual payment date - check both entries and manual_payment_dates table
  // Use local date format to avoid timezone issues
  const activeDateStr = formatDateLocal(activeDate);
  
  // First check manual_payment_dates table (manualPaymentDateTimes is an array of {date, time} objects)
  const hasManualPaymentDateFromTable = manualPaymentDateTimes.some(({ date }) => {
    if (!date) return false;
    // date is already a string in YYYY-MM-DD format from the API
    const dateStr = typeof date === 'string' ? date.split('T')[0] : formatDateLocal(new Date(date));
    return dateStr === activeDateStr;
  });
  
  // Also check entries (from any day) that have this date as payment date
  const hasManualPaymentDateFromEntries = Object.values(calendarEntries).flat().some(entry => {
    if (!entry.manualPaymentDate) return false;
    // Handle both string and Date formats
    let paymentDate;
    if (typeof entry.manualPaymentDate === 'string') {
      // Parse string date (YYYY-MM-DD format)
      if (entry.manualPaymentDate.includes('T')) {
        paymentDate = new Date(entry.manualPaymentDate);
      } else {
        // Parse YYYY-MM-DD format - use local time to avoid timezone issues
        const parts = entry.manualPaymentDate.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const day = parseInt(parts[2], 10);
          paymentDate = new Date(year, month, day);
        } else {
          paymentDate = new Date(entry.manualPaymentDate + 'T00:00:00');
        }
      }
    } else {
      paymentDate = new Date(entry.manualPaymentDate);
    }
    
    // Validate date
    if (isNaN(paymentDate.getTime())) {
      return false;
    }
    
    // Compare date strings to avoid timezone issues
    const paymentDateStr = formatDateLocal(paymentDate);
    return paymentDateStr === activeDateStr;
  });
  
  const hasManualPaymentDate = hasManualPaymentDateFromTable || hasManualPaymentDateFromEntries;
  const activePaymentSchedule = getPaymentSchedule();
  const activeDateIsExcludedPaymentDay = excludedPaymentDates.includes(activeDateStr);
  const activeDateIsScheduledPaymentDay = activePaymentSchedule && isPaymentDay(activeDate, activePaymentSchedule) && !activeDateIsExcludedPaymentDay;
  const activeDateIsPaymentDate = hasManualPaymentDate || activeDateIsScheduledPaymentDay;
  const showMarkPaidWithPaymentModal =
    activeDateIsPaymentDate && !isPaymentCompleted(activeDate, activePaymentSchedule);

  const getStartDateForCumulativeUnpaid = () => {
    const flat = Object.values(calendarEntries).flat();
    let minTs = null;
    flat.forEach((e) => {
      if (e.startTime === '00:00' && e.endTime === '00:00') return;
      if (e.isPaid || e.is_paid) return;
      const t = new Date(e.date).getTime();
      if (!Number.isFinite(t)) return;
      if (minTs === null || t < minTs) minTs = t;
    });
    if (minTs !== null) {
      const d = new Date(minTs);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return new Date(activeDate.getFullYear(), activeDate.getMonth(), 1);
  };

  const openMarkAllPaidToActiveDate = () => {
    setMarkAllPaidDialog({
      isOpen: true,
      type: 'selected',
      endDate: new Date(activeDate),
      startDate: getStartDateForCumulativeUnpaid()
    });
  };

  const activityLogGroups = useMemo(() => {
    const map = new Map();
    for (const log of monthAuditLogs) {
      let dayKey;
      if (log.entryDate != null) {
        const ed = log.entryDate;
        dayKey = typeof ed === 'string' ? ed.split('T')[0] : formatDateLocal(new Date(ed));
      } else if (log.createdAt) {
        dayKey = formatDateLocal(new Date(log.createdAt));
      } else {
        continue;
      }
      if (!map.has(dayKey)) map.set(dayKey, []);
      map.get(dayKey).push(log);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [monthAuditLogs]);

  const formatActivityDayLabel = (isoDay) => {
    const parts = isoDay.split('-').map(Number);
    if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) return isoDay;
    const [yy, mm, dd] = parts;
    return new Date(yy, mm - 1, dd).toLocaleDateString('sr-Latn-RS', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 p-4 lg:p-6 space-y-4 max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
              <MdCalendarToday className="text-2xl text-primary-600 dark:text-primary-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('calendar.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('calendar.subtitle') || t('calendar.manageSchedule') || ''}
              </p>
            </div>
          </div>
          
          <div className={`min-w-[260px] max-w-full ${showBabysitterMenu ? 'relative z-50' : ''}`}>
            {friends.length === 0 ? (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/40 rounded-2xl p-3 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex items-start gap-2">
                  <MdWarning className="text-lg text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="mb-1">{t('calendar.noBabysitter') || 'Trenutno nemate unetu bebisiterku.'}</p>
                    <button
                      onClick={() => navigate('/dashboard/friends')}
                      className="text-primary-600 dark:text-primary-300 hover:underline font-medium"
                    >
                      {t('calendar.clickToAdd') || 'Kliknite ovde za unos'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="surface-card p-2">
                <div className="relative">
                  <button
                    ref={babysitterTriggerRef}
                    onClick={() => setShowBabysitterMenu(!showBabysitterMenu)}
                    className="flex items-center justify-between gap-2 w-full px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/40 text-sm text-gray-900 dark:text-gray-100 transition-colors"
                  >
                    <div className="flex flex-col items-start gap-0 min-w-0">
                      <div className="flex items-center gap-2 min-w-0 w-full">
                        <MdPerson className="text-lg text-primary-600 dark:text-primary-300 flex-shrink-0" />
                        <span className="font-medium truncate">
                          {selectedBabysitter
                            ? (calendarDisplayName || (friends[0]?.name || friends[0]?.email) || t('calendar.selectBabysitter') || 'Izaberite bebisiterku')
                            : (friends[0]?.name || friends[0]?.email || t('calendar.selectBabysitter') || 'Izaberite bebisiterku')}
                        </span>
                        {(() => {
                          const currentFriend = selectedBabysitter || friends[0];
                          if (currentFriend && !currentFriend.hasAccount) {
                            return (
                              <span className="text-xs text-yellow-600 dark:text-yellow-400 flex-shrink-0">
                                ({t('friends.notMember') || 'Nije član'})
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                    <MdSettings className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0" />
                  </button>
                  <DropdownOrBottomSheet
                    open={showBabysitterMenu}
                    onClose={() => setShowBabysitterMenu(false)}
                    triggerRef={babysitterTriggerRef}
                    title={selectedBabysitter?.relationshipType === 'family' ? (t('calendar.selectBabysitterForFamily') || 'Izaberite bebisiterku za porodicu') : (t('calendar.selectBabysitter') || 'Izaberite bebisiterku')}
                  >
                    {friends.filter(f => f.relationshipType === 'babysitter').map((friend) => (
                      <button
                        key={friend.id}
                        onClick={() => {
                          setSelectedBabysitter(friend);
                          if (friend.hourlyRate) setDefaultHourlyRate(String(friend.hourlyRate));
                          if (friend.flexibleRates) setFlexibleRates(friend.flexibleRates);
                          setShowBabysitterMenu(false);
                          // Reload calendar data when switching to another babysitter (own vs shared) so amounts match
                          loadData(friend);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          selectedBabysitter?.id === friend.id ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <MdPerson className="text-lg flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {selectedBabysitter?.id === friend.id
                                ? (calendarDisplayName || friend.name || friend.email)
                                : (friend.name || friend.email)}
                            </p>
                            {friend.hourlyRate && <p className="text-xs text-gray-500 dark:text-gray-400">{friend.hourlyRate} RSD/h</p>}
                            {friend.isShared && friend.sharedByName && (
                              <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                {t('calendar.sharedBy') || 'Podeljeno od'}: {friend.sharedByName}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                    <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => {
                          if (selectedBabysitter?.relationshipType === 'family') {
                            navigate(`/dashboard/friends?familyId=${selectedBabysitter.id}`);
                          } else {
                            navigate('/dashboard/friends');
                          }
                          setShowBabysitterMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-primary-600 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                      >
                        {t('calendar.manageBabysitters') || 'Upravljaj bebisiterkama'}
                      </button>
                    </div>
                  </DropdownOrBottomSheet>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Metrics - Reorganized */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          {/* 1. Dugovanje za mesec + Ukupno dugovanje - Sjedinjene */}
          <div className="surface-card relative pl-4 pr-2 py-2 sm:p-3 overflow-hidden min-w-0">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-olive-600 rounded-r" />
            <div className="flex items-center gap-2 mb-1">
              <MdAccountBalanceWallet className="text-xl text-olive-600 flex-shrink-0" />
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 truncate min-w-0">
                {t('calendar.debtForMonth') || 'Dugovanje za'} {monthNames[viewMonth]}
              </h3>
            </div>
            <p className="font-bold text-olive-700 dark:text-olive-300 mb-1 whitespace-nowrap overflow-hidden text-ellipsis min-w-0" style={{ fontSize: 'clamp(0.875rem, 4.5vw, 1.25rem)' }}>
              {displayMonthlyDebt != null ? formatCurrency(displayMonthlyDebt) : (useServerDebtForDisplay ? '…' : formatCurrency(0))}
            </p>
            {displayMonthlyDebt != null && displayTotalDebt != null ? (
              displayTotalDebt > displayMonthlyDebt ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('calendar.totalDebt') || 'Ukupno dugovanje'}: <span className="font-semibold text-gray-700 dark:text-gray-300">{formatCurrency(displayTotalDebt)}</span>
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('calendar.noPreviousDebt') || 'Nema prethodnog dugovanja'}
                </p>
              )
            ) : useServerDebtForDisplay && serverDebtSummary == null ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">Učitavanje…</p>
            ) : null}
          </div>
          
          {/* 2. Sledeće plaćanje */}
          <div className="surface-card relative pl-4 pr-2 py-2 sm:p-3 overflow-hidden min-w-0">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500 rounded-r" />
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <MdSchedule className="text-xl text-amber-500" />
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                  {t('calendar.nextPayment') || 'Sledeće plaćanje'}
                </h3>
              </div>
            </div>
            {selectedBabysitter ? (
              nextPaymentDate ? (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">
                    {calendarDisplayName || selectedBabysitter.name || selectedBabysitter.email}
                  </p>
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400 mb-1">
                    {daysUntilPayment !== null && daysUntilPayment > 0 ? `${daysUntilPayment} ${daysUntilPayment === 1 ? t('calendar.day') || 'dan' : t('calendar.days') || 'dana'}` : t('calendar.today') || 'Danas'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {nextPaymentDate.toLocaleDateString('sr-RS', { day: 'numeric', month: 'long' })}
                  </p>
                </>
              ) : !selectedBabysitter.paymentSchedule ? (
                <>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    {t('calendar.noSchedule') || 'Nije podešeno'}
                  </p>
                  <button
                    onClick={() => navigate('/dashboard/friends')}
                    className="text-xs text-amber-600 dark:text-amber-400 hover:underline font-semibold"
                  >
                    {t('calendar.setupPayment') || 'Podesi plaćanja'}
                  </button>
                </>
              ) : null
            ) : selectedBabysitter?.relationshipType === 'family' ? (
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {friends.filter(f => f.relationshipType === 'babysitter').length > 0 ? (
                  friends.filter(f => f.relationshipType === 'babysitter').map((babysitter) => {
                    const nextPayment = getNextPaymentForBabysitter(babysitter);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const daysUntil = nextPayment ? Math.max(0, Math.ceil((nextPayment.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))) : null;
                    return (
                      <div key={babysitter.id} className="text-xs">
                        <p className="font-semibold text-gray-700 dark:text-gray-300 truncate">
                          {babysitter.name || babysitter.email}
                        </p>
                        {nextPayment ? (
                          <p className="text-amber-600 dark:text-amber-400 text-[10px]">
                            {daysUntil === 0 ? (t('calendar.today') || 'Danas') : `${daysUntil} ${daysUntil === 1 ? (t('calendar.day') || 'dan') : (t('calendar.days') || 'dana')}`}
                          </p>
                        ) : babysitter.paymentSchedule ? (
                          <p className="text-gray-400 dark:text-gray-500 text-[10px]">
                            {t('calendar.noUpcoming') || 'Nema nadolazećeg'}
                          </p>
                        ) : (
                          <p className="text-gray-400 dark:text-gray-500 text-[10px]">
                            {t('calendar.noSchedule') || 'Nije podešeno'}
                          </p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('calendar.noBabysitters') || 'Nema bebisiterki'}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {friends.filter(f => f.relationshipType === 'babysitter').length > 0 ? (
                  friends.filter(f => f.relationshipType === 'babysitter').map((babysitter) => {
                    const nextPayment = getNextPaymentForBabysitter(babysitter);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const daysUntil = nextPayment ? Math.max(0, Math.ceil((nextPayment.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))) : null;
                    return (
                      <div key={babysitter.id} className="text-xs">
                        <p className="font-semibold text-gray-700 dark:text-gray-300 truncate">
                          {babysitter.name || babysitter.email}
                        </p>
                        {nextPayment ? (
                          <p className="text-amber-600 dark:text-amber-400 text-[10px]">
                            {daysUntil === 0 ? (t('calendar.today') || 'Danas') : `${daysUntil} ${daysUntil === 1 ? (t('calendar.day') || 'dan') : (t('calendar.days') || 'dana')}`}
                          </p>
                        ) : babysitter.paymentSchedule ? (
                          <p className="text-gray-400 dark:text-gray-500 text-[10px]">
                            {t('calendar.noUpcoming') || 'Nema nadolazećeg'}
                          </p>
                        ) : (
                          <p className="text-gray-400 dark:text-gray-500 text-[10px]">
                            {t('calendar.noSchedule') || 'Nije podešeno'}
                          </p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('calendar.selectBabysitter') || 'Izaberite bebisiterku'}
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* 3. Sati rada */}
          <div className="surface-card relative pl-4 pr-2 py-2 sm:p-3 overflow-hidden min-w-0">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-olive-600 rounded-r" />
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="flex items-center gap-2">
                <MdAccessTime className="text-lg text-olive-600" />
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                  {t('calendar.totalHours') || 'Ukupno sati'}
                </h3>
              </div>
              <span className="text-lg font-bold text-olive-700 dark:text-olive-300">
                {monthTotalHours.toFixed(1)}h
              </span>
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('calendar.totalHoursThisMonth') || 'Ukupno sati ovaj mesec'}
              </span>
              {unpaidMonthHours > 0 && (
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                  {unpaidMonthHours.toFixed(1)}h {t('calendar.unpaidHoursThisMonth') || 'neplaćeno'}
                </span>
              )}
            </div>
          </div>
          
          {/* 4. Radnih dana ovog meseca - Poslednja kartica */}
          <div className="surface-card pl-4 pr-2 py-2 sm:p-3 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <MdCalendarToday className="text-xl text-gray-500 dark:text-gray-400" />
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                {t('calendar.workingDays') || 'Radnih dana ovog meseca'}
              </h3>
            </div>
            <p className="text-xl font-bold text-primary-600 mb-1">
              {realizedWorkingDays} / {workingDaysThisMonth}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('calendar.realized') || 'Realizovano'} / {t('calendar.total') || 'Ukupno'}
            </p>
          </div>
        </div>

        {/* Top row: Kalendar podeljen sa (left) + Pregled + three-dots (right) */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              {(() => {
                if (selectedBabysitter?.isShared && selectedBabysitter?.sharedByName) {
                  return (
                    <>
                      <MdShare className="text-sm text-primary-500 flex-shrink-0" />
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {t('calendar.sharedWithShort') || t('calendar.sharedWith') || 'Podeljeno sa'}:{' '}
                        <strong className="text-gray-800 dark:text-gray-200">{selectedBabysitter.sharedByName}</strong>
                      </span>
                    </>
                  );
                }
                const sharedWith = friends.filter(f => !f.isShared && (f.calendarShared || (f.shareSettings && f.shareSettings.calendar)));
                if (sharedWith.length === 0) return null;
                const names = sharedWith.map(f => f.name || f.email || t('friends.friend') || 'Prijatelj').join(', ');
                return (
                  <>
                    <MdShare className="text-sm text-primary-500 flex-shrink-0" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {t('calendar.sharedWithShort') || t('calendar.sharedWith') || 'Podeljeno sa'}: <strong className="text-gray-800 dark:text-gray-200">{names}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => setSharedPermissionsOpen(true)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                      title={t('calendar.sharedPermissions') || 'Dozvole korisnika'}
                      aria-label={t('calendar.sharedPermissions') || 'Dozvole korisnika'}
                    >
                      <MdInfoOutline className="text-lg" />
                    </button>
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Mobile: icon toggle calendar vs list; desktop: same */}
              <div
                className="flex rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden"
                role="group"
                aria-label={t('calendar.viewMode') || 'Prikaz kalendara'}
              >
                <button
                  type="button"
                  onClick={() => setCalendarViewMode('calendar')}
                  className={`p-2 sm:px-2.5 sm:py-2 transition-colors ${calendarViewMode === 'calendar' ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300' : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  title={t('calendar.calendarView') || 'Kalendar'}
                  aria-pressed={calendarViewMode === 'calendar'}
                >
                  <MdCalendarToday className="text-lg sm:text-xl" />
                </button>
                <button
                  type="button"
                  onClick={() => setCalendarViewMode('list')}
                  className={`p-2 sm:px-2.5 sm:py-2 transition-colors border-l border-gray-200 dark:border-gray-600 ${calendarViewMode === 'list' ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300' : 'bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  title={t('calendar.listView') || 'Lista'}
                  aria-pressed={calendarViewMode === 'list'}
                >
                  <MdViewList className="text-lg sm:text-xl" />
                </button>
              </div>
              {selectedBabysitter && (
                <div className="relative" ref={autoFillMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowAutoFillMenu(!showAutoFillMenu)}
                    className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                    title={t('calendar.calendarActions') || 'Akcije nad kalendarom'}
                  >
                    <MdSettings className="text-lg text-gray-500 dark:text-gray-400" />
                  </button>
                  <DropdownOrBottomSheet
                    open={showAutoFillMenu}
                    onClose={() => setShowAutoFillMenu(false)}
                    triggerRef={autoFillMenuRef}
                    title={t('calendar.calendarActions') || 'Akcije nad kalendarom'}
                  >
                    {selectedBabysitter?.isShared ? (
                      <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        <p>{t('calendar.sharedReadOnlyHint') || 'Samo pregled – izmene nisu dozvoljene.'}</p>
                      </div>
                    ) : (
                    <>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const today = new Date();
                          const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
                          const ftw = selectedBabysitter.fullTimeWork;
                          const startTime = (ftw?.enabled && ftw?.startTime) || '09:00';
                          const endTime = (ftw?.enabled && ftw?.endTime) || '17:00';
                          const workingDays = (ftw?.enabled && ftw?.days?.length) ? ftw.days : [1, 2, 3, 4, 5];
                          const allEntriesResponse = await api.get('/calendar-entries', { params: calendarEntriesQueryParams });
                          const allEntries = Array.isArray(allEntriesResponse.data) ? allEntriesResponse.data : [];
                          const entriesToCreate = [];
                          for (let date = new Date(firstDayOfMonth); date <= today; date.setDate(date.getDate() + 1)) {
                            const dayOfWeek = date.getDay();
                            if (!workingDays.includes(dayOfWeek)) continue;
                            const dateISO = formatDateLocal(date);
                            const existing = allEntries.find(e => {
                              const eDate = new Date(e.date).toISOString().split('T')[0];
                              return eDate === dateISO && e.startTime !== '00:00' && e.endTime !== '00:00';
                            });
                            if (!existing) entriesToCreate.push({ date: dateISO, startTime, endTime });
                          }
                          const hourlyRate = selectedBabysitter.hourlyRate || parseFloat(defaultHourlyRate) || null;
                          await Promise.all(entriesToCreate.map(({ date, startTime, endTime }) =>
                            api.post('/calendar-entries', { date, startTime, endTime, notes: 'Automatski unos - puno radno vreme', hourlyRate, ...calendarMutationBody })
                          ));
                          await loadData();
                          setShowAutoFillMenu(false);
                          showToast(t('calendar.autoFillSuccess')?.replace('{count}', entriesToCreate.length) || `Kreirano ${entriesToCreate.length} unosa.`, 'success');
                        } catch (error) {
                          showToast(t('calendar.autoFillError') || 'Greška: ' + (error.response?.data?.message || error.message), 'error');
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdAdd className="text-base" />
                      {t('calendar.autoFillToToday') || 'Popuni do današnjeg dana'}
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
                          const endDate = new Date(activeDate);
                          endDate.setHours(23, 59, 59, 999);
                          const ftw = selectedBabysitter.fullTimeWork;
                          const startTime = (ftw?.enabled && ftw?.startTime) || '09:00';
                          const endTime = (ftw?.enabled && ftw?.endTime) || '17:00';
                          const workingDays = (ftw?.enabled && ftw?.days?.length) ? ftw.days : [1, 2, 3, 4, 5];
                          const allEntriesResponse = await api.get('/calendar-entries', { params: calendarEntriesQueryParams });
                          const allEntries = Array.isArray(allEntriesResponse.data) ? allEntriesResponse.data : [];
                          const entriesToCreate = [];
                          for (let date = new Date(firstDayOfMonth); date <= endDate; date.setDate(date.getDate() + 1)) {
                            const dayOfWeek = date.getDay();
                            if (!workingDays.includes(dayOfWeek)) continue;
                            const dateISO = formatDateLocal(date);
                            const existing = allEntries.find(e => {
                              const eDate = new Date(e.date).toISOString().split('T')[0];
                              return eDate === dateISO && e.startTime !== '00:00' && e.endTime !== '00:00';
                            });
                            if (!existing) entriesToCreate.push({ date: dateISO, startTime, endTime });
                          }
                          const hourlyRate = selectedBabysitter.hourlyRate || parseFloat(defaultHourlyRate) || null;
                          await Promise.all(entriesToCreate.map(({ date, startTime, endTime }) =>
                            api.post('/calendar-entries', { date, startTime, endTime, notes: 'Automatski unos - puno radno vreme', hourlyRate, ...calendarMutationBody })
                          ));
                          await loadData();
                          setShowAutoFillMenu(false);
                          showToast(t('calendar.autoFillToSelectedSuccess')?.replace('{count}', entriesToCreate.length) || `Kreirano ${entriesToCreate.length} unosa.`, 'success');
                        } catch (error) {
                          showToast(t('calendar.autoFillError') || 'Greška: ' + (error.response?.data?.message || error.message), 'error');
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdAdd className="text-base" />
                      {t('calendar.autoFillToSelected') || 'Popuni do označenog datuma'}
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
                          const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
                          const ftw = selectedBabysitter.fullTimeWork;
                          const startTime = (ftw?.enabled && ftw?.startTime) || '09:00';
                          const endTime = (ftw?.enabled && ftw?.endTime) || '17:00';
                          const workingDays = (ftw?.enabled && ftw?.days?.length) ? ftw.days : [1, 2, 3, 4, 5];
                          const allEntriesResponse = await api.get('/calendar-entries', { params: calendarEntriesQueryParams });
                          const allEntries = Array.isArray(allEntriesResponse.data) ? allEntriesResponse.data : [];
                          const entriesToCreate = [];
                          for (let date = new Date(firstDayOfMonth); date <= lastDayOfMonth; date.setDate(date.getDate() + 1)) {
                            const dayOfWeek = date.getDay();
                            if (!workingDays.includes(dayOfWeek)) continue;
                            const dateISO = formatDateLocal(date);
                            const existing = allEntries.find(e => {
                              const eDate = new Date(e.date).toISOString().split('T')[0];
                              return eDate === dateISO && e.startTime !== '00:00' && e.endTime !== '00:00';
                            });
                            if (!existing) entriesToCreate.push({ date: dateISO, startTime, endTime });
                          }
                          const hourlyRate = selectedBabysitter.hourlyRate || parseFloat(defaultHourlyRate) || null;
                          await Promise.all(entriesToCreate.map(({ date, startTime, endTime }) =>
                            api.post('/calendar-entries', { date, startTime, endTime, notes: 'Automatski unos - puno radno vreme', hourlyRate, ...calendarMutationBody })
                          ));
                          await loadData();
                          setShowAutoFillMenu(false);
                          showToast(t('calendar.autoFillMonthSuccess')?.replace('{count}', entriesToCreate.length) || `Kreirano ${entriesToCreate.length} unosa.`, 'success');
                        } catch (error) {
                          showToast(t('calendar.autoFillError') || 'Greška: ' + (error.response?.data?.message || error.message), 'error');
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdAdd className="text-base" />
                      {t('calendar.autoFillMonth') || 'Popuni za ceo mesec'}
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post('/calendar-entries/test-fulltime-scheduler');
                          await loadData();
                          setShowAutoFillMenu(false);
                          showToast(t('calendar.autoFillRunNowSuccess') || 'Automatski unos pokrenut.', 'success');
                        } catch (error) {
                          showToast(t('calendar.autoFillError') || 'Greška: ' + (error.response?.data?.message || error.message), 'error');
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdSchedule className="text-base" />
                      {t('calendar.autoFillRunNow') || 'Pokreni automatski unos sada'}
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      type="button"
                      onClick={() => { setShowAutoFillMenu(false); setMarkAllPaidDialog({ isOpen: true, type: 'today', endDate: new Date(), startDate: getStartDateForCumulativeUnpaid() }); }}
                      className="w-full text-left px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdCheckCircle className="text-base" />
                      {t('calendar.markAllPaidToToday') || 'Označi sve kao plaćeno do danas'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAutoFillMenu(false); setMarkAllPaidDialog({ isOpen: true, type: 'selected', endDate: new Date(activeDate), startDate: getStartDateForCumulativeUnpaid() }); }}
                      className="w-full text-left px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdCheckCircle className="text-base" />
                      {t('calendar.markAllPaidToSelected') || 'Označi sve kao plaćeno do datuma'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAutoFillMenu(false); setMarkAllPaidDialog({ isOpen: true, type: 'month', endDate: new Date(viewYear, viewMonth + 1, 0), startDate: new Date(viewYear, viewMonth, 1) }); }}
                      className="w-full text-left px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdCheckCircle className="text-base" />
                      {t('calendar.markAllPaidMonth') || 'Označi sve kao plaćeno za mesec'}
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      type="button"
                      onClick={() => { setDeleteAllMonthConfirm({ isOpen: true }); setShowAutoFillMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdDelete className="text-base" />
                      {t('calendar.deleteAllMonth') || 'Ukloni sva vremena za ovaj mesec'}
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      type="button"
                      onClick={() => { setShowAutoFillMenu(false); setSharedPermissionsOpen(true); }}
                      className="w-full text-left px-4 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdShare className="text-base" />
                      {t('calendar.shareWithFriend') || 'Podeli kalendar sa prijateljem'}
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                    <button
                      type="button"
                      onClick={() => {
                        setShowAutoFillMenu(false);
                        setActivityLogOpen(true);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <MdHistory className="text-base text-gray-400 flex-shrink-0" />
                      {t('calendar.activityLogMenu') || 'Istorija aktivnosti'}
                    </button>
                    </>
                    )}
                  </DropdownOrBottomSheet>
                </div>
              )}
            </div>
          </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Calendar or List */}
          <div className="lg:col-span-2 min-w-0">
            {calendarViewMode === 'list' ? (
              <div className="surface-card p-3 sm:p-4 min-w-0 overflow-hidden flex flex-col min-h-0 min-h-[240px] max-h-[280px] sm:min-h-[320px] sm:max-h-[360px] lg:max-h-[420px]">
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                  <button
                    onClick={() => handleMonthNavigation(-1)}
                    aria-label={t('calendar.previousMonth') || 'Prethodni mesec'}
                    className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    <MdChevronLeft className="text-lg" />
                  </button>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{monthNames[viewMonth]} {viewYear}</h2>
                  <button
                    onClick={() => handleMonthNavigation(1)}
                    aria-label={t('calendar.nextMonth') || 'Sledeći mesec'}
                    className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    <MdChevronRight className="text-lg" />
                  </button>
                </div>
                <div className="space-y-2 flex-1 min-h-0 overflow-y-auto">
                  {selectedMonthEntries.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">{t('calendar.noEntries') || 'Nema unosa ovog meseca'}</p>
                  ) : (
                    selectedMonthEntries.map(([dateKey, entries]) => {
                      const date = new Date(dateKey);
                      const totalMinutes = entries.reduce((sum, entry) => sum + getDurationMinutes(entry.startTime, entry.endTime), 0);
                      const allPaid = entries.length > 0 && entries.every(entry => entry.isPaid);
                      return (
                        <div
                          key={dateKey}
                          className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-primary-50/60 dark:hover:bg-primary-900/10 cursor-pointer"
                          onClick={() => setActiveDate(date)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                {allPaid && <MdCheckCircle className="text-[10px] text-green-600 dark:text-green-400 flex-shrink-0" />}
                                <p className={`text-[10px] font-semibold ${allPaid ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                  {date.getDate()}. {monthNames[date.getMonth()]}
                                </p>
                              </div>
                              {entries.some(e => e.startTime !== '00:00' || e.endTime !== '00:00') && (
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{formatDuration(totalMinutes)}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
            <div className="surface-card p-3 sm:p-4 min-w-0 overflow-hidden">
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => handleMonthNavigation(-1)}
                  aria-label={t('calendar.previousMonth') || 'Prethodni mesec'}
                  className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  <MdChevronLeft className="text-lg" />
                </button>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {monthNames[viewMonth]} {viewYear}
                </h2>
                <button
                  onClick={() => handleMonthNavigation(1)}
                  aria-label={t('calendar.nextMonth') || 'Sledeći mesec'}
                  className="p-2 rounded-xl bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  <MdChevronRight className="text-lg" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2 sm:mb-3">
                {dayNames.map((day) => (
                  <div key={day} className="min-w-0 text-center text-[8px] sm:text-[10px] font-semibold text-gray-600 dark:text-gray-400 py-0.5 truncate" title={day}>
                    {day.length > 4 ? day.slice(0, 2) : day}
                  </div>
                ))}

                {calendarDays.map((day, index) => {
                  const dateObj = day ? new Date(viewYear, viewMonth, day) : null;
                  const entries = dateObj ? getEntriesForDate(dateObj) : [];
                  const totalMinutes = entries.reduce((sum, entry) => sum + getDurationMinutes(entry.startTime, entry.endTime), 0);
                  const isToday = dateObj && dateObj.toDateString() === today.toDateString();
                  const isActive = dateObj && dateObj.toDateString() === activeDate.toDateString();
                  // Check if Saturday (6) or Sunday (0)
                  const dayOfWeek = dateObj ? dateObj.getDay() : -1;
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  // Check payment status
                  const allPaid = entries.length > 0 && entries.every(entry => entry.isPaid);
                  
                  
                  // Check if this is a payment day
                  const paymentSchedule = getPaymentSchedule();
                  const dateStr = dateObj ? dateObj.toISOString().split('T')[0] : null;
                  const isExcluded = dateStr && excludedPaymentDates.includes(dateStr);
                  const isPayDay = dateObj && paymentSchedule && isPaymentDay(dateObj, paymentSchedule) && !isExcluded;
                  const paymentCompleted = isPayDay && isPaymentCompleted(dateObj, paymentSchedule);
                  
                  // Check if this day has a manual payment date - check both manualPaymentDates table and entries
                  const currentDateStr = dateObj ? formatDateLocal(dateObj) : null;
                  
                  // First check manual_payment_dates table (manualPaymentDateTimes is an array of {date, time} objects)
                  const hasManualPaymentDateFromTable = currentDateStr ? manualPaymentDateTimes.some(({ date }) => {
                    if (!date) return false;
                    // date is already a string in YYYY-MM-DD format from the API
                    const dateStr = typeof date === 'string' ? date.split('T')[0] : formatDateLocal(new Date(date));
                    return dateStr === currentDateStr;
                  }) : false;
                  
                  // Also check entries (from any day) that have this date as payment date
                  const hasManualPaymentDateFromEntries = currentDateStr ? Object.values(calendarEntries).flat().some(entry => {
                    if (!entry.manualPaymentDate) return false;
                    // Handle both string and Date formats
                    let paymentDate;
                    if (typeof entry.manualPaymentDate === 'string') {
                      if (entry.manualPaymentDate.includes('T')) {
                        paymentDate = new Date(entry.manualPaymentDate);
                      } else {
                        const parts = entry.manualPaymentDate.split('-');
                        if (parts.length === 3) {
                          const year = parseInt(parts[0], 10);
                          const month = parseInt(parts[1], 10) - 1;
                          const day = parseInt(parts[2], 10);
                          paymentDate = new Date(year, month, day);
                        } else {
                          paymentDate = new Date(entry.manualPaymentDate + 'T00:00:00');
                        }
                      }
                    } else {
                      paymentDate = new Date(entry.manualPaymentDate);
                    }
                    if (isNaN(paymentDate.getTime())) return false;
                    const paymentDateStr = formatDateLocal(paymentDate);
                    return paymentDateStr === currentDateStr;
                  }) : false;
                  
                  const isManualPaymentDay = hasManualPaymentDateFromTable || hasManualPaymentDateFromEntries;
                  
                  // Check if this is an unpaid payment day that is DUE TODAY (needs red background)
                  // Only show red if the payment date is today and not paid
                  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
                  const dateOnly = dateObj ? new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 0, 0, 0, 0) : null;
                  const isTodayPaymentDay = dateOnly && dateOnly.getTime() === todayStart.getTime();
                  const isUnpaidPaymentDay = isTodayPaymentDay && (isPayDay || isManualPaymentDay) && !paymentCompleted && !allPaid;
                  
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (dateObj) {
                          setActiveDate(dateObj);
                          setSelectedDaySheetOpen(true);
                        }
                      }}
                      disabled={!dateObj}
                      className={`
                        min-w-0 w-full aspect-[1.36] sm:aspect-[1.64] rounded-lg sm:rounded-xl border transition-colors text-[10px] sm:text-[12px] leading-tight flex items-center justify-center p-0.5 sm:p-1
                        ${!dateObj ? 'border-transparent cursor-default' : 'cursor-pointer'}
                        ${isUnpaidPaymentDay
                          ? 'border-gray-200 dark:border-gray-700 ring-1 ring-amber-300 dark:ring-amber-700 bg-white/80 dark:bg-gray-800/40'
                          : isActive
                          ? 'border-primary-500 ring-2 ring-primary-200/70 dark:ring-primary-900/40 bg-primary-50 dark:bg-primary-900/10'
                          : isToday
                          ? 'border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-800/40'
                          : isWeekend && dateObj
                          ? 'border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/60'
                          : 'border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/40 hover:bg-gray-50 dark:hover:bg-gray-700/30'}
                      `}
                    >
                      {dateObj && (
                        <div className="flex flex-col items-center justify-center gap-0 min-w-0 overflow-hidden w-full">
                          <div className="flex items-center justify-center gap-px sm:gap-0.5 min-w-0 max-w-full">
                            {(isPayDay || isManualPaymentDay) ? (
                              <span
                                className={`flex-shrink-0 inline-flex items-center justify-center w-2.5 h-2.5 sm:w-4 sm:h-4 rounded-full border leading-none ${
                                  (paymentCompleted || allPaid)
                                    ? 'bg-olive-100 border-olive-300 text-olive-700'
                                    : 'bg-primary-100 border-primary-200 text-primary-600'
                                }`}
                                title={
                                  (paymentCompleted || allPaid)
                                    ? (t('calendar.paymentCompleted') || 'Plaćanje završeno')
                                    : (t('calendar.paymentDue') || 'Plaćanje dospeva')
                                }
                              >
                                {(paymentCompleted || allPaid) ? (
                                  <MdCheckCircle className="text-[7px] sm:text-[10px]" />
                                ) : (
                                  <MdAttachMoney className="text-[7px] sm:text-[10px]" />
                                )}
                              </span>
                            ) : (entries.length > 0 ? (
                              <span
                                className={`flex-shrink-0 inline-block w-1.5 h-1.5 rounded-full ${allPaid ? 'bg-olive-600' : 'bg-amber-500'}`}
                                title={allPaid ? (t('calendar.paid') || 'Plaćeno') : (t('calendar.unpaid') || 'Neplaćeno')}
                              />
                            ) : null)}
                            <span className="font-semibold text-[10px] sm:text-[13px] text-gray-900 dark:text-gray-100 truncate">
                              {day}
                            </span>
                          </div>
                          {entries.length > 0 && (() => {
                            const hasRealTime = entries.some(e => e.startTime !== '00:00' || e.endTime !== '00:00');
                            if (!hasRealTime) return null;
                            return (
                              <div className="hidden sm:flex flex-col items-center gap-0">
                                <span className={`text-[6px] sm:text-[8px] font-semibold ${
                                  allPaid 
                                    ? 'text-olive-700 dark:text-olive-300' 
                                    : 'text-amber-600 dark:text-amber-400'
                                }`}>
                                  {formatDuration(totalMinutes)}
                                </span>
                              </div>
                            );
                          })()}
                          {entries.length > 1 && (
                            <span className="hidden sm:inline text-[8px] text-gray-500 dark:text-gray-400">
                              {entries.length}x
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-1 flex flex-col min-h-0 min-w-0 overflow-visible">
            {/* Selected Day - desktop: sidebar (same height as calendar); content spread across full height */}
            <div className="hidden lg:flex lg:flex-col lg:flex-1 surface-panel min-w-0 min-h-0 overflow-hidden p-4">
              {/* Header: title + date + summary — compact at top */}
              <div className="flex-shrink-0 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('calendar.selectedDay') || 'Izabrani dan'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {formatDisplayDate(activeDate, monthNames)}
                </p>
                {effectiveOwnerId && effectiveCalendarWriteAllowed && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('calendar.editingSharedCalendarHint') || 'Uređujete deljeni kalendar (imate dozvolu za pisanje).'}
                  </p>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {displayEntries.length > 0
                    ? `${formatDuration(selectedDayMinutes)} · ${displayEntries.length} ${t('calendar.timeSlots') || 'vremenskih slotova'}`
                    : t('calendar.noEntriesDay') || 'Nema unosa za ovaj dan'}
                </p>
                {/* Payment due hint */}
                {(() => {
                  const paymentSchedule = getPaymentSchedule();
                  const activeDateStr = formatDateLocal(activeDate);
                  const isExcluded = excludedPaymentDates.includes(activeDateStr);
                  const isPayDayFromSchedule = paymentSchedule && isPaymentDay(activeDate, paymentSchedule);
                  const isPayDay = isPayDayFromSchedule && !isExcluded;
                  if (isPayDay && paymentSchedule?.time) {
                    return (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                        <MdSchedule className="text-xs flex-shrink-0" />
                        {t('calendar.paymentDue') || 'Plaćanje dospeva'} u {paymentSchedule.time}
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Actions block — separate section, full-width buttons with labels */}
              <div className="flex-shrink-0 flex flex-col gap-2 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => openNewEntryModal(activeDate)}
                  className="btn-outline-primary flex items-center justify-center gap-2 text-sm py-2.5 px-3 w-full"
                >
                  <MdAdd className="text-base" />
                  {t('calendar.addTimeSlot') || 'Unesi vreme'}
                </button>
                {activeDateIsPaymentDate ? (
                  <div className="flex flex-col gap-2">
                    {showMarkPaidWithPaymentModal && (
                      <button
                        type="button"
                        onClick={openMarkAllPaidToActiveDate}
                        className="btn-primary px-3 py-2.5 text-sm flex items-center justify-center gap-2 w-full"
                      >
                        <MdCheckCircle className="text-base" />
                        {t('calendar.markAllPaidToSelected') || 'Označi sve kao plaćeno do datuma'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setRemovePaymentDateConfirm({ isOpen: true })}
                      className="text-sm py-2.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium w-full text-center"
                    >
                      {t('calendar.removePaymentDate') || 'Ukloni datum plaćanja'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={openPaymentDateModal}
                    className="text-sm py-2.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold w-full text-center"
                  >
                    {t('calendar.setPaymentDate') || 'Postavi datum plaćanja'}
                  </button>
                )}
              </div>

              {/* Entries list — fills remaining space */}
              <div className="space-y-2 flex-1 min-h-0 overflow-y-auto">
                {/* Show payment date placeholder entries first (if any) */}
                {placeholderEntries.length > 0 && placeholderEntries.map((entry) => {
                  const paymentSchedule = getPaymentSchedule();
                  const paymentTime = paymentSchedule?.time || null;
                  const paymentDate = new Date(entry.manualPaymentDate);
                  const dateStr = paymentDate.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short', year: 'numeric' });
                  return (
                    <div
                      key={entry.id}
                      className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/40"
                    >
                      <div className="flex items-center gap-2">
                        <MdAttachMoney className="text-lg text-amber-500" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                            {t('calendar.paymentDue') || 'Plaćanje dospeva'}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                            {dateStr}{paymentTime && paymentTime !== '00:00' && ` u ${paymentTime}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Show other placeholder entries (00:00-00:00 without payment date) that might be paid */}
                {otherPlaceholderEntries.length > 0 && otherPlaceholderEntries.map((entry) => {
                  return (
                    <div
                      key={entry.id}
                      className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-900/20"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {entry.isPaid && (
                            <MdCheckCircle className="text-lg text-green-600 dark:text-green-400" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              {t('calendar.emptyEntry') || 'Prazan unos'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {entry.isPaid ? (t('calendar.paid') || 'Plaćeno') : (t('calendar.unpaid') || 'Neplaćeno')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              if (!canMutateCalendarEntry(entry)) {
                                showToast(t('calendar.cannotMarkShared') || 'Ne možete označiti deljene unose kao plaćene.', 'info');
                                return;
                              }
                              try {
                                await api.put(`/calendar-entries/${entry.id}/mark-paid`, { 
                                  isPaid: !entry.isPaid,
                                  ...calendarMutationBody
                                });
                                await loadData();
                                showToast(
                                  entry.isPaid 
                                    ? (t('calendar.dayMarkedAsUnpaid') || 'Dan je označen kao neplaćen.')
                                    : (t('calendar.dayMarkedAsPaid') || 'Dan je označen kao plaćen.'),
                                  'success'
                                );
                              } catch (error) {
                                console.error('Error marking entry:', error);
                                const detail = error.response?.data?.message || error.message;
                                showToast(detail ? `${t('calendar.markAsPaidError') || 'Greška pri označavanju.'} (${detail})` : (t('calendar.markAsPaidError') || 'Greška pri označavanju.'), 'error');
                              }
                            }}
                            disabled={!canMutateCalendarEntry(entry)}
                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium ${
                              entry.isPaid
                                ? 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                                : 'hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400'
                            } ${!canMutateCalendarEntry(entry) ? 'opacity-40 cursor-not-allowed' : ''}`}
                            title={!canMutateCalendarEntry(entry)
                              ? (t('calendar.cannotMarkShared') || 'Ne možete označiti deljene unose kao plaćene.')
                              : (entry.isPaid ? (t('calendar.markDayRowAsUnpaid') || 'Označi dan kao neplaćen') : (t('calendar.markDayRowAsPaid') || 'Označi dan kao plaćen'))}
                          >
                            <MdCheckCircle className="text-sm" />
                            <span>{entry.isPaid ? (t('calendar.markDayRowAsUnpaidShort') || 'Neplaćeno') : (t('calendar.markDayRowAsPaidShort') || 'Plaćeno')}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteConfirm({ isOpen: true, entry: entry });
                            }}
                            className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium"
                            title={t('calendar.delete') || 'Obriši'}
                          >
                            <MdDelete className="text-sm" />
                            <span>{t('calendar.delete') || 'Obriši'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Show real time entries */}
                {displayEntries.length === 0 && placeholderEntries.length === 0 && otherPlaceholderEntries.length === 0 ? (
                  // If no entries are shown but calendar shows a checkmark, there might be a hidden paid entry
                  selectedDayEntries.length > 0 ? (
                    // Show all entries that exist but might be hidden
                    selectedDayEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-900/20"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {entry.isPaid && (
                              <MdCheckCircle className="text-lg text-green-600 dark:text-green-400" />
                            )}
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {entry.startTime === '00:00' && entry.endTime === '00:00' 
                                  ? (t('calendar.emptyEntry') || 'Prazan unos')
                                  : `${entry.startTime} - ${entry.endTime}`}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {entry.isPaid ? (t('calendar.paid') || 'Plaćeno') : (t('calendar.unpaid') || 'Neplaćeno')}
                                {entry.manualPaymentDate && ` · ${t('calendar.paymentDate') || 'Datum plaćanja'}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={async () => {
                                if (!canMutateCalendarEntry(entry)) {
                                  showToast(t('calendar.cannotMarkShared') || 'Ne možete označiti deljene unose kao plaćene.', 'info');
                                  return;
                                }
                                try {
                                  await api.put(`/calendar-entries/${entry.id}/mark-paid`, { 
                                    isPaid: !entry.isPaid,
                                    ...calendarMutationBody
                                  });
                                  await loadData();
                                  showToast(
                                    entry.isPaid 
                                      ? (t('calendar.dayMarkedAsUnpaid') || 'Dan je označen kao neplaćen.')
                                      : (t('calendar.dayMarkedAsPaid') || 'Dan je označen kao plaćen.'),
                                    'success'
                                  );
                                } catch (error) {
                                  console.error('Error marking entry:', error);
                                  const detail = error.response?.data?.message || error.message;
                                  showToast(detail ? `${t('calendar.markAsPaidError') || 'Greška pri označavanju.'} (${detail})` : (t('calendar.markAsPaidError') || 'Greška pri označavanju.'), 'error');
                                }
                              }}
                              disabled={!canMutateCalendarEntry(entry)}
                              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium ${
                                entry.isPaid
                                  ? 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                                  : 'hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400'
                              } ${!canMutateCalendarEntry(entry) ? 'opacity-40 cursor-not-allowed' : ''}`}
                              title={!canMutateCalendarEntry(entry)
                                ? (t('calendar.cannotMarkShared') || 'Ne možete označiti deljene unose kao plaćene.')
                                : (entry.isPaid ? (t('calendar.markDayRowAsUnpaid') || 'Označi dan kao neplaćen') : (t('calendar.markDayRowAsPaid') || 'Označi dan kao plaćen'))}
                            >
                              <MdCheckCircle className="text-sm" />
                              <span>{entry.isPaid ? (t('calendar.markDayRowAsUnpaidShort') || 'Neplaćeno') : (t('calendar.markDayRowAsPaidShort') || 'Plaćeno')}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteConfirm({ isOpen: true, entry: entry });
                              }}
                              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium"
                              title={t('calendar.delete') || 'Obriši'}
                            >
                              <MdDelete className="text-sm" />
                              <span>{t('calendar.delete') || 'Obriši'}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
                      {t('calendar.noEntriesDay') || 'No entries for this day'}
                    </p>
                  )
                ) : (
                  displayEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white/70 dark:bg-gray-800/40 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5">
                            {entry.isPaid ? (
                              <MdCheckCircle className="text-sm text-olive-600" title={t('calendar.paid') || 'Paid'} />
                            ) : (
                              <MdPending className="text-sm text-amber-500" title={t('calendar.unpaid') || 'Unpaid'} />
                            )}
                            {/* Don't show time if it's a placeholder entry (00:00-00:00) */}
                            {!(entry.startTime === '00:00' && entry.endTime === '00:00') && (
                              <p className={`text-xs font-medium ${
                                entry.isPaid 
                                  ? 'text-olive-700 dark:text-olive-300' 
                                  : 'text-gray-800 dark:text-gray-100'
                              }`}>
                                {entry.startTime} - {entry.endTime}
                              </p>
                            )}
                            {/* Show payment date info if it's a placeholder entry */}
                            {entry.startTime === '00:00' && entry.endTime === '00:00' && entry.manualPaymentDate && (
                              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                                {t('calendar.paymentDate') || 'Datum plaćanja'}
                              </p>
                            )}
                          </div>
                          {entry.notes && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5 italic">
                              {entry.notes}
                            </p>
                          )}
                          {/* Show payment date info only if it's not a placeholder entry (real time entry with payment date) */}
                          {entry.manualPaymentDate && !(entry.startTime === '00:00' && entry.endTime === '00:00') && (() => {
                            const paymentSchedule = getPaymentSchedule();
                            const paymentTime = paymentSchedule?.time || null;
                            const paymentDate = new Date(entry.manualPaymentDate);
                            const dateStr = paymentDate.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short', year: 'numeric' });
                            return (
                              <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 font-semibold flex items-center gap-1">
                                <MdSchedule className="text-[10px]" />
                                {t('calendar.paymentDate') || 'Datum plaćanja'}: {dateStr}
                                {paymentTime && paymentTime !== '00:00' && ` u ${paymentTime}`}
                              </p>
                            );
                          })()}
                          {entry.hourlyRate !== null && entry.hourlyRate !== undefined && entry.hourlyRate > 0 ? (
                            <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 font-semibold">
                              {formatCurrency(entry.hourlyRate)}/h · {formatCurrency((getDurationMinutes(entry.startTime, entry.endTime) / 60) * entry.hourlyRate)}
                            </p>
                          ) : !(entry.startTime === '00:00' && entry.endTime === '00:00') && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                              {t('calendar.noRateSet') || 'Cena nije postavljena'}
                            </p>
                          )}
                        </div>
                        {/* Don't show duration for placeholder entries */}
                        {!(entry.startTime === '00:00' && entry.endTime === '00:00') && (
                          <div className="text-right">
                            <span className={`text-xs font-bold block ${
                              entry.isPaid 
                                ? 'text-olive-700 dark:text-olive-300' 
                                : 'text-amber-600 dark:text-amber-400'
                            }`}>
                              {formatDuration(getDurationMinutes(entry.startTime, entry.endTime))}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 mt-2 w-full">
                        <div className="flex gap-2 w-full">
                          <button
                            type="button"
                            onClick={() => openEditEntryModal(entry)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-medium"
                            title={t('calendar.edit') || 'Izmeni'}
                          >
                            <MdEdit className="text-sm" />
                            <span>{t('calendar.edit') || 'Izmeni'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteEntry(entry)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-medium"
                            title={t('calendar.delete') || 'Obriši'}
                          >
                            <MdDelete className="text-sm" />
                            <span>{t('calendar.delete') || 'Obriši'}</span>
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!canMutateCalendarEntry(entry)) {
                              showToast(t('calendar.cannotMarkShared') || 'Ne možete označiti deljene unose kao plaćene.', 'info');
                              return;
                            }
                            try {
                              await api.put(`/calendar-entries/${entry.id}/mark-paid`, { 
                                isPaid: !entry.isPaid,
                                ...calendarMutationBody
                              });
                              await loadData();
                              showToast(
                                entry.isPaid 
                                  ? (t('calendar.dayMarkedAsUnpaid') || 'Dan je označen kao neplaćen.')
                                  : (t('calendar.dayMarkedAsPaid') || 'Dan je označen kao plaćen.'),
                                'success'
                              );
                            } catch (error) {
                              console.error('Error marking entry as paid:', error);
                              const detail = error.response?.data?.message || error.message;
                              showToast(detail ? `${t('calendar.markPaidError') || 'Greška pri označavanju.'} (${detail})` : (t('calendar.markPaidError') || 'Greška pri označavanju.'), 'error');
                            }
                          }}
                          disabled={!canMutateCalendarEntry(entry)}
                          className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium ${
                            entry.isPaid
                              ? 'border border-amber-200 dark:border-amber-800 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                              : 'border border-green-200 dark:border-green-800 bg-green-50/80 dark:bg-green-900/20 hover:bg-green-100/90 dark:hover:bg-green-900/35 text-green-700 dark:text-green-300'
                          } ${!canMutateCalendarEntry(entry) ? 'opacity-40 cursor-not-allowed' : ''}`}
                          title={!canMutateCalendarEntry(entry)
                            ? (t('calendar.cannotMarkShared') || 'Ne možete označiti deljene unose kao plaćene.')
                            : (entry.isPaid ? (t('calendar.markDayRowAsUnpaid') || 'Označi dan kao neplaćen') : (t('calendar.markDayRowAsPaid') || 'Označi dan kao plaćen'))}
                        >
                          <MdCheckCircle className="text-base" />
                          <span>{entry.isPaid ? (t('calendar.markDayRowAsUnpaidShort') || 'Neplaćeno') : (t('calendar.markDayRowAsPaidShort') || 'Plaćeno')}</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Mobile: Izabrani dan bottom sheet (opens when user taps a day) — portal so it appears above bottom tab bar */}
            {selectedDaySheetOpen && createPortal(
              <div className="lg:hidden fixed inset-0 z-[9998]" aria-modal="true" role="dialog">
                <div
                  className="fixed inset-0 bg-black/40 z-[9998]"
                  onClick={() => setSelectedDaySheetOpen(false)}
                  aria-hidden
                />
                <div className="fixed left-0 right-0 bottom-0 max-h-[88vh] flex flex-col bg-white dark:bg-gray-800 rounded-t-2xl shadow-xl border-t border-gray-200 dark:border-gray-700 z-[9999] pb-safe">
                  <div className="flex items-center justify-between gap-2 p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {t('calendar.selectedDay') || 'Izabrani dan'}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDisplayDate(activeDate, monthNames)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedDaySheetOpen(false)}
                      className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                      aria-label="Zatvori"
                    >
                      <MdClose className="text-xl" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0 p-3">
                    <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                      {displayEntries.length > 0
                        ? `${formatDuration(selectedDayMinutes)} · ${displayEntries.length} ${t('calendar.timeSlots') || 'vremenskih slotova'}`
                        : t('calendar.noEntriesDay') || 'Nema unosa za ovaj dan'}
                    </p>
                    <button
                      type="button"
                      onClick={() => { openNewEntryModal(activeDate); setSelectedDaySheetOpen(false); }}
                      className="btn-outline-primary w-full flex items-center justify-center gap-2 text-xs mb-4"
                    >
                      <MdAdd className="text-sm" />
                      {t('calendar.addTimeSlot') || 'Unesi vreme'}
                    </button>
                    <div className="space-y-2">
                      {placeholderEntries.length > 0 && placeholderEntries.map((entry) => {
                        const paymentSchedule = getPaymentSchedule();
                        const paymentTime = paymentSchedule?.time || null;
                        const paymentDate = new Date(entry.manualPaymentDate);
                        const dateStr = paymentDate.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short', year: 'numeric' });
                        return (
                          <div
                            key={entry.id}
                            className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/40"
                          >
                            <div className="flex items-center gap-2">
                              <MdAttachMoney className="text-lg text-amber-500" />
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                                  {t('calendar.paymentDue') || 'Plaćanje dospeva'}
                                </p>
                                <p className="text-[10px] text-gray-600 dark:text-gray-300 mt-0.5">
                                  {dateStr}{paymentTime && paymentTime !== '00:00' && ` u ${paymentTime}`}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {otherPlaceholderEntries.length > 0 && otherPlaceholderEntries.map((entry) => (
                        <div
                          key={entry.id}
                          className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-900/20 flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-2">
                            {entry.isPaid && <MdCheckCircle className="text-lg text-green-600 dark:text-green-400" />}
                            <div>
                              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('calendar.emptyEntry') || 'Prazan unos'}</p>
                              <p className="text-[9px] text-gray-500 dark:text-gray-400">{entry.isPaid ? (t('calendar.paid') || 'Plaćeno') : (t('calendar.unpaid') || 'Neplaćeno')}</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button type="button" onClick={() => { setDeleteConfirm({ isOpen: true, entry }); setSelectedDaySheetOpen(false); }} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"><MdDelete className="text-xs" /></button>
                          </div>
                        </div>
                      ))}
                      {displayEntries.length === 0 && placeholderEntries.length === 0 && otherPlaceholderEntries.length === 0 && selectedDayEntries.length > 0
                        ? selectedDayEntries.map((entry) => (
                            <div key={entry.id} className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-900/20 flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                {entry.isPaid && <MdCheckCircle className="text-lg text-green-600 dark:text-green-400" />}
                                <div>
                                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                    {entry.startTime === '00:00' && entry.endTime === '00:00' ? (t('calendar.emptyEntry') || 'Prazan unos') : `${entry.startTime} - ${entry.endTime}`}
                                  </p>
                                  <p className="text-[9px] text-gray-500 dark:text-gray-400">{entry.isPaid ? (t('calendar.paid') || 'Plaćeno') : (t('calendar.unpaid') || 'Neplaćeno')}</p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <button type="button" onClick={() => { setDeleteConfirm({ isOpen: true, entry }); setSelectedDaySheetOpen(false); }} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"><MdDelete className="text-xs" /></button>
                              </div>
                            </div>
                          ))
                        : displayEntries.map((entry) => (
                            <div key={entry.id} className="p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white/70 dark:bg-gray-800/40">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-1.5">
                                    {entry.isPaid ? <MdCheckCircle className="text-[12px] text-olive-600" /> : <MdPending className="text-[12px] text-amber-500" />}
                                    {!(entry.startTime === '00:00' && entry.endTime === '00:00') && (
                                      <p className={`text-[10px] font-medium ${entry.isPaid ? 'text-olive-700 dark:text-olive-300' : 'text-gray-800 dark:text-gray-100'}`}>
                                        {entry.startTime} - {entry.endTime}
                                      </p>
                                    )}
                                  </div>
                                  {entry.notes && <p className="text-[9px] text-gray-500 dark:text-gray-500 mt-0.5 italic">{entry.notes}</p>}
                                  {entry.hourlyRate !== null && entry.hourlyRate !== undefined && entry.hourlyRate > 0 ? (
                                    <p className="text-[10px] text-gray-600 dark:text-gray-300 mt-0.5 font-semibold">
                                      {formatCurrency(entry.hourlyRate)}/h · {formatCurrency((getDurationMinutes(entry.startTime, entry.endTime) / 60) * entry.hourlyRate)}
                                    </p>
                                  ) : !(entry.startTime === '00:00' && entry.endTime === '00:00') && (
                                    <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5 italic">{t('calendar.noRateSet') || 'Cena nije postavljena'}</p>
                                  )}
                                </div>
                                <span className={`text-[10px] font-bold ${entry.isPaid ? 'text-olive-700 dark:text-olive-300' : 'text-amber-600 dark:text-amber-400'}`}>
                                  {formatDuration(getDurationMinutes(entry.startTime, entry.endTime))}
                                </span>
                              </div>
                              <div className="flex flex-col gap-2 mt-2 w-full">
                                <div className="flex gap-2 w-full">
                                  <button
                                    type="button"
                                    onClick={() => { openEditEntryModal(entry); setSelectedDaySheetOpen(false); }}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-[10px] font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <MdEdit className="text-xs" />
                                    {t('calendar.edit') || 'Izmeni'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { handleDeleteEntry(entry); setSelectedDaySheetOpen(false); }}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-red-200 dark:border-red-800 text-[10px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <MdDelete className="text-xs" />
                                    {t('calendar.delete') || 'Obriši'}
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!canMutateCalendarEntry(entry)) { showToast(t('calendar.cannotMarkShared') || 'Ne možete označiti deljene unose.', 'info'); return; }
                                    try {
                                      await api.put(`/calendar-entries/${entry.id}/mark-paid`, { isPaid: !entry.isPaid, ...calendarMutationBody });
                                      await loadData();
                                      showToast(entry.isPaid ? (t('calendar.dayMarkedAsUnpaid') || 'Označeno kao neplaćeno.') : (t('calendar.dayMarkedAsPaid') || 'Označeno kao plaćeno.'), 'success');
                                    } catch (err) {
                                      showToast(err.response?.data?.message || t('calendar.markPaidError') || 'Greška', 'error');
                                    }
                                  }}
                                  disabled={!canMutateCalendarEntry(entry)}
                                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-[10px] font-medium ${
                                    entry.isPaid
                                      ? 'border border-amber-200 dark:border-amber-800 text-yellow-700 dark:text-yellow-300'
                                      : 'border border-green-200 dark:border-green-800 bg-green-50/80 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                                  } ${!canMutateCalendarEntry(entry) ? 'opacity-40 cursor-not-allowed' : ''}`}
                                >
                                  <MdCheckCircle className="text-sm" />
                                  {entry.isPaid ? (t('calendar.markDayRowAsUnpaidShort') || 'Neplaćeno') : (t('calendar.markDayRowAsPaidShort') || 'Plaćeno')}
                                </button>
                              </div>
                            </div>
                          ))}
                    </div>
                    {activeDateIsPaymentDate ? (
                      <div className="mt-4 flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => { openMarkAllPaidToActiveDate(); setSelectedDaySheetOpen(false); }}
                          className="btn-primary w-full text-xs flex items-center justify-center gap-2 py-2.5 px-3"
                        >
                          <MdCheckCircle className="text-sm" />
                          {t('calendar.markAllPaidToSelected') || 'Označi sve kao plaćeno do datuma'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRemovePaymentDateConfirm({ isOpen: true });
                            setSelectedDaySheetOpen(false);
                          }}
                          className="text-red-600 dark:text-red-400 hover:underline text-xs font-medium w-full text-center py-1"
                        >
                          {t('calendar.removePaymentDate') || 'Ukloni datum plaćanja'}
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => { openPaymentDateModal(); setSelectedDaySheetOpen(false); }} className="mt-4 text-primary-600 dark:text-primary-400 hover:underline text-xs font-semibold w-full text-left">
                        {t('calendar.setPaymentDate') || 'Postavi datum plaćanja'}
                      </button>
                    )}
                  </div>
                </div>
              </div>,
              document.body
            )}

          </div>
        </div>
      </div>

      {/* Mesecna istorija aktivnosti — otvara se iz menija podešavanja (zupčanik) */}
      {activityLogOpen &&
        createPortal(
          isMobile ? (
            <>
              <div
                className="fixed inset-0 bg-black/40 z-[9998]"
                onClick={() => setActivityLogOpen(false)}
                aria-hidden
              />
              <div
                className="fixed left-0 right-0 bottom-0 z-[9999] bg-white dark:bg-gray-800 rounded-t-2xl shadow-xl border-t border-gray-200 dark:border-gray-700 pb-safe max-h-[88vh] flex flex-col"
                role="dialog"
                aria-modal="true"
                aria-labelledby="activity-log-title"
              >
                <div className="pt-3 pb-2 flex-shrink-0">
                  <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600 mx-auto" />
                </div>
                <div className="px-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3 flex-shrink-0">
                  <h3 id="activity-log-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100 pr-2">
                    {(t('calendar.activityLogTitle') || 'Istorija aktivnosti') + ` · ${monthNames[viewMonth]} ${viewYear}`}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setActivityLogOpen(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                    aria-label={t('common.close') || 'Zatvori'}
                  >
                    <MdClose className="text-xl" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3">
                  {activityLogLoading ? (
                    <p className="text-sm text-gray-500 py-10 text-center">{t('common.loading') || 'Učitavanje...'}</p>
                  ) : activityLogGroups.length === 0 ? (
                    <p className="text-sm text-gray-500 py-8 text-center">
                      {t('calendar.activityLogEmpty') || 'Nema zabeleženih aktivnosti za ovaj mesec.'}
                    </p>
                  ) : (
                    <div className="space-y-5">
                      {activityLogGroups.map(([dayKey, logs]) => (
                        <div key={dayKey}>
                          <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            {formatActivityDayLabel(dayKey)}
                          </p>
                          <ul className="space-y-2">
                            {logs.map((log) => {
                              const detail = formatAuditLogDetailLine(log);
                              return (
                              <li
                                key={log.id}
                                className="text-[11px] sm:text-xs text-gray-700 dark:text-gray-300 leading-snug pl-2 border-l-2 border-primary-200 dark:border-primary-700 pb-1"
                              >
                                <div className="font-medium text-gray-800 dark:text-gray-200">
                                  {formatAuditActionLabel(log.action)}
                                </div>
                                {detail ? (
                                  <p className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed">
                                    {detail}
                                  </p>
                                ) : null}
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                                  {log.actorName || (t('calendar.someone') || 'Neko')}
                                  {' · '}
                                  {new Date(log.createdAt).toLocaleString('sr-RS', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </li>
                            );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setActivityLogOpen(false)}
                    className="btn-primary w-full"
                  >
                    {t('common.close') || 'Zatvori'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div
              className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
              onClick={() => setActivityLogOpen(false)}
            >
              <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="activity-log-title-desktop"
              >
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <h3
                    id="activity-log-title-desktop"
                    className="text-base font-semibold text-gray-900 dark:text-gray-100 pr-2"
                  >
                    {(t('calendar.activityLogTitle') || 'Istorija aktivnosti') + ` · ${monthNames[viewMonth]} ${viewYear}`}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setActivityLogOpen(false)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label={t('common.close') || 'Zatvori'}
                  >
                    <MdClose className="text-xl" />
                  </button>
                </div>
                <div className="overflow-y-auto flex-1 min-h-0 p-4">
                  {activityLogLoading ? (
                    <p className="text-sm text-gray-500 py-10 text-center">{t('common.loading') || 'Učitavanje...'}</p>
                  ) : activityLogGroups.length === 0 ? (
                    <p className="text-sm text-gray-500 py-8 text-center">
                      {t('calendar.activityLogEmpty') || 'Nema zabeleženih aktivnosti za ovaj mesec.'}
                    </p>
                  ) : (
                    <div className="space-y-5 max-h-[min(60vh,520px)]">
                      {activityLogGroups.map(([dayKey, logs]) => (
                        <div key={dayKey}>
                          <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            {formatActivityDayLabel(dayKey)}
                          </p>
                          <ul className="space-y-2">
                            {logs.map((log) => {
                              const detail = formatAuditLogDetailLine(log);
                              return (
                              <li
                                key={log.id}
                                className="text-xs text-gray-700 dark:text-gray-300 leading-snug pl-2 border-l-2 border-primary-200 dark:border-primary-700 pb-1"
                              >
                                <div className="font-medium text-gray-800 dark:text-gray-200">
                                  {formatAuditActionLabel(log.action)}
                                </div>
                                {detail ? (
                                  <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5 leading-relaxed">
                                    {detail}
                                  </p>
                                ) : null}
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                                  {log.actorName || (t('calendar.someone') || 'Neko')}
                                  {' · '}
                                  {new Date(log.createdAt).toLocaleString('sr-RS', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </li>
                            );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setActivityLogOpen(false)}
                    className="btn-primary w-full"
                  >
                    {t('common.close') || 'Zatvori'}
                  </button>
                </div>
              </div>
            </div>
          ),
          document.body
        )}

      {/* Shared calendar permissions: dialog on desktop, bottom sheet on mobile */}
      {sharedPermissionsOpen && (() => {
        const sharedWith = friends.filter(f => !f.isShared && (f.calendarShared || (f.shareSettings && f.shareSettings.calendar)));
        const title = t('calendar.sharedPermissions') || 'Dozvole korisnika';
        const content = (
          <div className="p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('calendar.sharedPermissionsHint') || 'Korisnici sa kojima delite kalendar mogu imati samo pregled ili i unos/unos izmena.'}
            </p>
            {sharedWith.length === 0 ? (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('calendar.noSharedUsers') || 'Nema korisnika sa kojima delite kalendar.'}</p>
                <button
                  type="button"
                  onClick={() => { setSharedPermissionsOpen(false); navigate('/dashboard/friends'); }}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <MdShare className="text-lg" />
                  {t('calendar.shareWithFriend') || 'Podeli kalendar sa prijateljem'}
                </button>
              </>
            ) : (
              <ul className="space-y-3">
                {sharedWith.map(f => (
                  <SharedPermissionRow
                    key={f.id}
                    friend={f}
                    onSave={async (calendarWrite) => {
                      try {
                        const current = f.shareSettings || {};
                        await api.post(`/friends/${f.id}/share-calendar`, {
                          shareCalendar: true,
                          sharePayments: !!current.payments,
                          shareBabysitters: !!current.babysitters,
                          shareCalendarWrite: !!calendarWrite
                        });
                        await loadData();
                        showToast(t('calendar.permissionsSaved') || 'Dozvole sačuvane.', 'success');
                      } catch (err) {
                        showToast((err.response?.data?.message) || err.message || t('calendar.permissionsError') || 'Greška pri čuvanju.', 'error');
                      }
                    }}
                  />
                ))}
              </ul>
            )}
            {sharedWith.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => { setSharedPermissionsOpen(false); navigate('/dashboard/friends'); }}
                  className="w-full py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg flex items-center justify-center gap-2"
                >
                  <MdShare className="text-base" />
                  {t('calendar.shareWithFriend') || 'Podeli kalendar sa prijateljem'}
                </button>
              </div>
            )}
          </div>
        );
        if (isMobile) {
          return createPortal(
            <>
              <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={() => setSharedPermissionsOpen(false)} aria-hidden />
              <div className="fixed left-0 right-0 bottom-0 z-[9999] bg-white dark:bg-gray-800 rounded-t-2xl shadow-xl border-t border-gray-200 dark:border-gray-700 pb-safe" data-dropdown-or-sheet role="dialog" aria-modal="true" aria-label={title}>
                <div className="pt-3 pb-2"><div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600 mx-auto" /></div>
                <p className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
                <div className="max-h-[70vh] overflow-y-auto py-2">{content}</div>
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <button type="button" onClick={() => setSharedPermissionsOpen(false)} className="btn-primary w-full">{t('common.close') || 'Zatvori'}</button>
                </div>
              </div>
            </>,
            document.body
          );
        }
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSharedPermissionsOpen(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                <button type="button" onClick={() => setSharedPermissionsOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><MdClose className="text-xl" /></button>
              </div>
              <div className="overflow-y-auto flex-1">{content}</div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" onClick={() => setSharedPermissionsOpen(false)} className="btn-primary w-full">{t('common.close') || 'Zatvori'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Entry Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingEntry ? (t('calendar.editEntry') || 'Edit Entry') : (t('calendar.addEntry') || 'Add Entry')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDisplayDate(activeDate, monthNames)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEntry(null);
                  setEntryForm(defaultEntryForm);
                  setRatePeriods([]);
                  setManualRates({});
                }}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('calendar.startTime') || 'Start Time'}
                  </label>
                  <input
                    type="time"
                    value={entryForm.startTime}
                    onChange={(e) => {
                      const newStartTime = e.target.value;
                      setEntryForm({ ...entryForm, startTime: newStartTime });
                      // Recalculate rate periods when time changes
                      if (newStartTime && entryForm.endTime) {
                        const periods = calculateRatePeriods(activeDate, newStartTime, entryForm.endTime, flexibleRates, defaultHourlyRate);
                        setRatePeriods(periods);
                        // Initialize manual rates
                        const manual = {};
                        periods.forEach((period, index) => {
                          manual[index] = period.rate;
                        });
                        setManualRates(manual);
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    {t('calendar.endTime') || 'End Time'}
                  </label>
                  <input
                    type="time"
                    value={entryForm.endTime}
                    onChange={(e) => {
                      const newEndTime = e.target.value;
                      setEntryForm({ ...entryForm, endTime: newEndTime });
                      // Recalculate rate periods when time changes
                      if (entryForm.startTime && newEndTime) {
                        const periods = calculateRatePeriods(activeDate, entryForm.startTime, newEndTime, flexibleRates, defaultHourlyRate);
                        setRatePeriods(periods);
                        // Initialize manual rates
                        const manual = {};
                        periods.forEach((period, index) => {
                          manual[index] = period.rate;
                        });
                        setManualRates(manual);
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              {/* Rate Periods - Show immediately when times are set */}
              {ratePeriods.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                    {t('calendar.hourlyRate') || 'Cena radnog sata (RSD/h)'}
                  </label>
                  <div className="space-y-3">
                    {ratePeriods.map((period, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {period.label} ({formatDuration(period.minutes)})
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={manualRates[index] !== undefined ? manualRates[index] : period.rate}
                            onChange={(e) => {
                              const newManualRates = { ...manualRates };
                              newManualRates[index] = parseFloat(e.target.value) || 0;
                              setManualRates(newManualRates);
                            }}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                          />
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                      <p className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                        {t('calendar.estimatedAmount') || 'Procenjena cena'}: {formatCurrency(
                          ratePeriods.reduce((sum, period, index) => {
                            const rate = manualRates[index] !== undefined ? manualRates[index] : period.rate;
                            return sum + ((period.minutes / 60) * rate);
                          }, 0)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('calendar.notes') || 'Notes'}
                </label>
                <textarea
                  value={entryForm.notes}
                  onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={t('calendar.notesPlaceholder') || 'Add notes...'}
                />
              </div>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => handleSaveEntry(false)}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <MdSave />
                  {t('common.save')}
                </button>
                {!editingEntry && (
                  <button
                    type="button"
                    onClick={() => handleSaveEntry(true)}
                    className="btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    <MdAdd />
                    {t('calendar.saveAndAddAnother') || 'Save and Add Another'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Date Modal */}
      {showPaymentDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('calendar.setPaymentDate') || 'Postavi datum plaćanja'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDisplayDate(activeDate, monthNames)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPaymentDateModal(false);
                  setPaymentTime('14:00');
                }}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  {t('calendar.paymentDateConfirm') || 'Da li želite da postavite datum plaćanja za ovaj dan?'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <strong>{formatDisplayDate(activeDate, monthNames)}</strong> će biti postavljen kao naredni datum plaćanja za podsetnik.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('calendar.paymentTime') || 'Vreme plaćanja'}
                </label>
                <input
                  type="time"
                  value={paymentTime}
                  onChange={(e) => setPaymentTime(e.target.value || '14:00')}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('calendar.paymentTimeHint') || 'Podrazumevano vreme je 14:00'}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={handleSetPaymentDate}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <MdSave />
                  {t('calendar.confirmPaymentDate') || 'Potvrdi'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentDateModal(false);
                    setPaymentTime('14:00');
                  }}
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                >
                  <MdClose />
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Mark as Paid Modal */}
      {showMarkPaidModal && markPaidDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('calendar.recordPaymentModalTitle') || 'Zabeleži uplatu'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDisplayDate(markPaidDate, monthNames)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowMarkPaidModal(false);
                  setMarkPaidAmount('');
                  setMarkPaidDate(null);
                }}
                className="p-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  {t('calendar.markAsPaidConfirm') || 'Označite dan kao plaćen i kreirajte zapis o plaćanju.'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('calendar.markAsPaidHint') || 'Iznos je izračunat na osnovu neplaćenih unosa do ovog datuma. Možete ga izmeniti.'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  {t('calendar.amount') || 'Iznos (RSD)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={markPaidAmount}
                  onChange={(e) => setMarkPaidAmount(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('calendar.amountHint') || 'Predloženi iznos na osnovu unosa do datuma plaćanja'}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleMarkAsPaidWithPayment}
                  className="btn-primary flex-1"
                >
                  {t('calendar.confirmMarkAsPaid') || 'Potvrdi i označi kao plaćeno'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowMarkPaidModal(false);
                    setMarkPaidAmount('');
                    setMarkPaidDate(null);
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, entry: null })}
        onConfirm={confirmDeleteEntry}
        title={t('calendar.deleteConfirmTitle') || 'Potvrda brisanja'}
        message={t('calendar.deleteConfirm') || 'Da li ste sigurni da želite da obrišete ovaj unos?'}
        confirmText={t('common.delete') || 'Obriši'}
        cancelText={t('common.cancel') || 'Otkaži'}
        type="danger"
      />
      
      <ConfirmDialog
        isOpen={deleteAllMonthConfirm.isOpen}
        onClose={() => setDeleteAllMonthConfirm({ isOpen: false })}
        onConfirm={confirmDeleteAllMonth}
        title={t('calendar.deleteAllMonthConfirmTitle') || 'Potvrda brisanja'}
        message={t('calendar.deleteAllMonthConfirm') || 'Da li ste sigurni da želite da obrišete sva vremena za ovaj mesec?'}
        confirmText={t('common.delete') || 'Obriši'}
        cancelText={t('common.cancel') || 'Otkaži'}
        type="danger"
      />

      <ConfirmDialog
        isOpen={removePaymentDateConfirm.isOpen}
        onClose={() => setRemovePaymentDateConfirm({ isOpen: false })}
        onConfirm={handleRemovePaymentDate}
        title={t('calendar.removePaymentDateConfirmTitle') || 'Ukloni datum plaćanja'}
        message={t('calendar.removePaymentDateConfirm') || 'Da li ste sigurni da želite da uklonite ovaj datum plaćanja sa kalendara?'}
        confirmText={t('calendar.removePaymentDate') || 'Ukloni'}
        cancelText={t('common.cancel') || 'Otkaži'}
        type="danger"
      />

      {markAllPaidDialog.isOpen && (
        <MarkAllPaidConfirmDialog
          type={markAllPaidDialog.type}
          startDate={markAllPaidDialog.startDate}
          endDate={markAllPaidDialog.endDate}
          onEndDateChange={(d) => setMarkAllPaidDialog(prev => ({ ...prev, endDate: d }))}
          onClose={() => setMarkAllPaidDialog({ isOpen: false, type: null, endDate: null })}
          onConfirm={async (customAmount) => {
            const { type, startDate, endDate } = markAllPaidDialog;
            if (!endDate) return;
            try {
              const startDateISO = formatDateLocal(startDate);
              const endDateISO = formatDateLocal(endDate);
              const allEntriesResponse = await api.get('/calendar-entries', { params: calendarEntriesQueryParams });
              const allEntries = Array.isArray(allEntriesResponse.data) ? allEntriesResponse.data : [];
              const entriesToMark = getEntriesToMarkAsPaid(allEntries, startDateISO, endDateISO);

              if (entriesToMark.length === 0) {
                showToast(t('calendar.noUnpaidEntries') || 'Nema neplaćenih unosa u ovom periodu.', 'info');
                setMarkAllPaidDialog({ isOpen: false, type: null, endDate: null });
                return;
              }

              const calculatedAmount = calculateAmountForEntries(entriesToMark);
              const amount = customAmount !== undefined && customAmount !== null ? customAmount : calculatedAmount;
              const periodStart = entriesToMark.length > 0
                ? new Date(Math.min(...entriesToMark.map(e => new Date(e.date).getTime())))
                : startDate;

              const paymentData = {
                amount: Math.round(amount * 100) / 100,
                date: endDateISO,
                method: 'bank_transfer',
                description: `Plaćanje za ${endDate.toLocaleDateString('sr-RS', { month: 'long', year: 'numeric' })}`,
                status: 'paid',
                periodStartDate: formatDateLocal(periodStart),
                periodEndDate: endDateISO,
                ...calendarMutationBody
              };

              await api.post('/payments', paymentData);
              // The POST above auto-marks entries paid on the server via markCalendarEntriesAsPaid.
              // We still send mark-paid PUTs for entries the viewer is explicitly allowed to mutate,
              // so isPaid is reflected immediately for own/owner-write rows even if the period scan misses any.
              const writableEntries = entriesToMark.filter(canMutateCalendarEntry);
              if (writableEntries.length > 0) {
                await Promise.allSettled(
                  writableEntries.map((entry) =>
                    api.put(`/calendar-entries/${entry.id}/mark-paid`, {
                      isPaid: true,
                      ...calendarMutationBody,
                    })
                  )
                );
              }
              await loadData();
              setMarkAllPaidDialog({ isOpen: false, type: null, endDate: null });
              showToast(
                (type === 'today' ? t('calendar.markAllPaidToTodaySuccess') : type === 'month' ? t('calendar.markAllPaidMonthSuccess') : t('calendar.markAllPaidToSelectedSuccess'))?.replace('{count}', entriesToMark.length) ||
                `Označeno ${entriesToMark.length} unosa. Kreirano plaćanje od ${formatCurrency(amount)}.`,
                'success'
              );
            } catch (error) {
              console.error('Error marking as paid:', error);
              showToast(t('calendar.markAllPaidError') || 'Greška pri označavanju: ' + (error.response?.data?.message || error.message), 'error');
            }
          }}
          getEntriesPreview={async () => {
            const { startDate, endDate } = markAllPaidDialog;
            if (!endDate) return { count: 0, amount: 0 };
            const startDateISO = formatDateLocal(startDate);
            const endDateISO = formatDateLocal(endDate);
            const allEntriesResponse = await api.get('/calendar-entries', { params: calendarEntriesQueryParams });
            const allEntries = Array.isArray(allEntriesResponse.data) ? allEntriesResponse.data : [];
            const entries = getEntriesToMarkAsPaid(allEntries, startDateISO, endDateISO);
            return { count: entries.length, amount: calculateAmountForEntries(entries) };
          }}
        />
      )}
    </div>
  );
}
